import { Dispatch, MouseEvent, SetStateAction, useEffect, useRef, useState, useCallback, useMemo, useLayoutEffect } from "react";
import { NavigateOptions, To, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import styles from "./MapScreen.module.css";
import Toast, { ToastData } from "../components/common/Toast";
import ProgressBar from "../components/common/ProgressBar";
import SideRail, { OverlapMapInfo } from "../components/map/siderail/SideRail";
import SideSheetHandle from "../components/map/sidesheet/SideSheetHandle";
import SideSheet from "../components/map/sidesheet/SideSheet";
import CategoryGroup from "../components/map/CategoryGroup";
import Map from "../components/map/Map";
import ScaleBar from "../components/map/ScaleBar";
import FloatingButton, { FloatingBtnOptions } from "../components/map/FloatingButton";
import POIListView, { POIListViewRef, SortingStatus } from "../components/map/sidesheet/POIListView";
import AddPlace from "../components/map/sidesheet/addplace/AddPlace";
import AddMultiplePlaces from "../components/map/sidesheet/addplace/AddMultiplePlaces";
import FloatingSideSheet from "../components/map/floatingsidesheet/FloatingSideSheet";
import AccountUpgrade from "../components/map/sidesheet/addplace/AccountUpgrade";
import SinglePlaceFormArea from "../components/map/sidesheet/addplace/SinglePlaceFormArea";
import { CategoryOptions, MapInfo, path, PlaceCoordsList, PrimaryInfo, PlaceInfoSet, AllPlaceData, PlaceData } from "../components/common/Common";
import { getColorByClass, getCircleRadius, formatNumber } from "../module/map/analysis";
import OverlapAnalysis from "../components/map/sidesheet/analysis/OverlapAnalysis";
import DirectionAnalysis, { Stage } from "../components/map/sidesheet/analysis/DirectionAnalysis";
import { useSlopeAnalysis } from "../hooks/useSlopeAnalysis";

import { RegionAnalysisLegend } from "../data_provider/gateways/AnalysisGateway";
import AccessibilityAnalysis from "../components/map/sidesheet/analysis/AccessibilityAnalysis";
import { TransportMode } from "../components/map/sidesheet/analysis/TransportSelector";
import Icon from "../components/common/Icon";
import DataProvider from "../data_provider/DataProvider";
import { Projection } from "ol/proj";
import { Map as OlMap } from "ol";
import { Polygon } from "ol/geom";

import { useModal } from "../components/modal/Modal";
import ModalButtons from "../components/modal/ModalButtons";
import LoadingSpinner from "../components/common/LoadingSpinner";

import LoginForm from "../components/login/LoginForm";
import SignUpModal from "../components/login/SignUpModal";
import Selector, { SelectorItems } from "../components/common/Selector";
import { DropdownSelectorItems } from "../components/common/DropdownSelector";
import AnalysisMetricSelector from "../components/common/AnalysisMetricSelector";
import { POISearchOptions } from "../components/map/sidesheet/SearchPOI";
import { ListSearchReqBody } from "../data_provider/gateways/MapGateway";
import LoadingCoverLayer from "../components/common/LoadingCoverLayer";
import { Helmet } from "react-helmet-async";
import { useLoginStatus } from "../hooks/useLoginStatus";
import { MarkerHighlightManager } from "../components/map/MarkerHighlightManager";
import ModalInput from "../components/modal/ModalInput";

/**
 * @interface
 * @prop {Dispatch<SetStateAction<boolean>>} setIsMap
 */
interface MapScreenProps {
    setIsMap: Dispatch<SetStateAction<boolean>>;
}

export const enum ContentList {
    Back = -1,
    List,
    AccountUpgrade,
    AddPlace,
    AddSinglePlace,
    AddMultiplePlaces,
    EditPlace,
    Direction,
    Accessibility,
    Overlap,
    Loading,
}

export enum MapToolType {
    Init = -2,
    None,

    Region,
    Choropleth,
    Chart,
    Bubble,
    Heat,
    Direction,
    Accessibility,
    ExtentAnalysis,
    Overlap,
    Slope,

    Distance = 10,
    Area,
    Radius,

    Add = 20,

    // 단계구분도, 히트맵 기능 추가
    Place,
    ThumbUp,
    View,
    Story,

    // 범위 분석
    DrawCustom,
    DrawCircle,
}

export interface AccessibilityState {
    vehicle?: TransportMode;
    ready: boolean;
}

interface MapToolOptions {
    [key: number]: FloatingBtnOptions;
}

const defaultCategory: CategoryOptions = {
    label: "전체",
    disabled: false,
};

/**
 * 맵 스크린 컴포넌트
 *
 * @author mjkang
 * @date 2024-08-20 10:45:10
 * @param props {@link MapScreenProps}
 * @returns MapScreen
 */
const MapScreen = (props: MapScreenProps) => {
    const [navigate, location] = [useNavigate(), useLocation()];

    // MapScreen에서 URL 파라미터 추출 시작
    const [params] = useSearchParams();
    // 기본 지도ID 추출 - 메인 지도 식별용
    const baseMapID = params.get("id");

    const POIList = useRef<HTMLDivElement>(null);
    const [mapInfos, setMapInfos] = useState<MapInfo[]>([]); // 기본 지도(인덱스 0) + 오버랩 지도들을 통합 관리
    const [allPlaceData, setAllPlaceData] = useState<AllPlaceData>({});
    const [categoriesArray, setCategoriesArray] = useState<CategoryOptions[][]>([[{ label: "전체", disabled: false }]]); // 각 지도별 카테고리 2차원 배열
    // 25.09.02 | Kim Young Jun | selectedCategory 제거하고 selectedCategoryArray만 사용
    const prevSelectedCategoryRef = useRef<number>(0);

    // 검색 옵션 state
    const [searchOptions, setSearchOptions] = useState<POISearchOptions | undefined>({
        category: undefined,
        search: undefined,
        XID: undefined,
    });

    const BUNDLE_SIZE = 40; // 로드할 데이터 개수
    const initSortingStatus: SortingStatus = { sortBy: "XID", direction: "asc" };
    const initRequestOption: ListSearchReqBody = {
        page: 1,
        count: BUNDLE_SIZE,
        filter: { category: undefined, search: undefined, XID: undefined },
        sortBy: initSortingStatus.sortBy,
        direction: initSortingStatus.direction,
    };

    // 검색 목록 옵션 state
    const [listSearchOptions, setListSearchOptions] = useState<ListSearchReqBody>(initRequestOption);

    const { ModalComponent, openModal, closeModal } = useModal();
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");

    // 사이드, 플로팅 시트 관련
    const [showSideSheet, setShowSideSheet] = useState(true);
    const [showFloatingSheet, setShowFloatingSheet] = useState(false);
    const [isClosedSideSheet, setIsClosedSideSheet] = useState(false);
    const sideRef = useRef<HTMLDivElement | null>(null);
    const floatingSideRef = useRef<HTMLDivElement | null>(null);
    const clearSelectedMarkerOnlyRef = useRef<(() => void) | null>(null);
    const clearSelectedMarkerAndCloseSheetRef = useRef<(() => void) | null>(null);

    // 사이드 시트 컨텐츠 관련 state ------------------------------
    const [sideSheetCont, setSideSheetCont] = useState(ContentList.List); // [28] => [hooks 넘버]
    const [prevSideSheetCont, setPrevSideSheetCont] = useState(ContentList.List); // [29]
    const [geocodingState, setGeocodingState] = useState(-1);
    const [sideSheetWidth, setSideSheetWidth] = useState(0); //[31] 좌측 사이드시트(sideRail포함) 영역 너비

    // 신규 장소 추가 관련 state -----------------------------------
    const [selectedNewPlace, setSelectedNewPlace] = useState<PlaceData>();
    const [newPlaceCoords, setNewPlaceCoords] = useState<PlaceCoordsList[]>([]); // index == fileIdx
    const [isAddMode, setIsAddMode] = useState(false);
    const [editPlaceStatus, setEditPlaceStatus] = useState<boolean>(false);
    const prevSelectedIdxRef = useRef<number | null>(null); // [36]
    const [newPlaceAddress, setNewPlaceAddress] = useState<string>("");
    const [revGeoLoading, setRevGeoLoading] = useState<boolean>(false);
    const [isOpenAgreeModal, setIsOpenAgreeModal] = useState<boolean>(false);

    // 맵 플로팅 버튼 관련 state --------------------
    const [showTopFloatingButtons, setShowTopFloatingButtons] = useState(true);
    const [activeIdx, _setActiveIdx] = useState(MapToolType.Init);
    // 250909 | smyun | 이전값 추적 ref 추가
    const activeIdxRef = useRef<MapToolType>(MapToolType.Init);
    const prevActiveIdxRef = useRef<MapToolType>(MapToolType.Init);

    const setActiveIdx = (next: MapToolType) => {
        // setState 호출 전에 prev를 스냅샷
        prevActiveIdxRef.current = activeIdxRef.current;
        activeIdxRef.current = next;
        _setActiveIdx(next);
    };
    // const [activeIdxRef, setActiveIdx] = [
    //     useRef(activeIdx),
    //     (idx: number) => {
    //         activeIdxRef.current = idx;
    //         _setActiveIdx(idx);
    //     },
    // ];
    const [isGPSOn, setIsGPSOn] = useState(false); // [40]
    const [isGPSLoading, setIsGPSLoading] = useState(false); // 2026.01.28 | dslee | GPS 로딩 상태 추가
    const [zoomLv, setZoomLv] = useState<number>(0);
    const [dropdownSelectorItems, setDropdownSelectorItems] = useState<DropdownSelectorItems[]>([]);
    const [selectedDropdownField, setSelectedDropdownField] = useState<string | null>(null);

    // POI 관련 state -------------------------------------------
    const [selectedPoiIdx, setSelectedPoiIdx] = useState<number | string>(0); // xid가 1부터 시작하므로 선택되지 않은 상태 == 0

    // 계산·분석도구 관련 state ----------------------------------------------
    const [blur, setBlur] = useState<number>(15); // heatmap
    const [legend, setLegend] = useState<RegionAnalysisLegend[]>();

    const [analysisXIDs, setAnalysisXIDs] = useState<number[] | undefined>(undefined); // 분석결과 XIDs (시간별 이동거리, 접근성 분석, 범위분석 공통) [45]
    const [clearByButton, setClearByButton] = useState<Stage>(-1); // 분석버튼 클릭 후 [이전 혹은 초기화]버튼으로 map source Clear 시 이용
    const [analysisStage, setAnalysisStage] = useState<Stage>(-1); // 분석 단계 기억

    const [isExtentAnalysis, setIsExtentAnalysis] = useState<boolean>(false); // 범위분석 활성화 여부

    const [selectedMapIdx, setSelectedMapIdx] = useState<number>(0); // 선택된 지도 인덱스 (0: 기준지도, 1+: 오버랩지도들)
    const [directionStartPoint, setDirectionStartPoint] = useState<number[] | undefined>(undefined); // direction 시작점 [50]
    const [directionVehicle, setDirectionVehicle] = useState<string>(""); // direction 탈것 정보
    const [mapProj, setMapProj] = useState<Projection>();
    const [isochronePolygon, setIsochronePolygon] = useState<Polygon | undefined>(undefined);
    const [accState, _setAccState] = useState<AccessibilityState>({ ready: false });
    const [resetAcc, setResetAcc] = useState<boolean>(false);

    // ScaleBar를 위한 Map 객체 상태
    const [currentMap, setCurrentMap] = useState<OlMap | undefined>(undefined);

    // 장소 유저 액트 state -------------------------------------------------
    const [thumbState, setThumbState] = useState(false);
    const [isBookmark, setIsBookmark] = useState(false); // [57]

    // 25.07.07 | Kim Young Jun | 동일 매트릭 선택 시에도 분석 동작하도록 상태 추가
    const [analysisTriggerId, setAnalysisTriggerId] = useState(0);
    // 25.07.10 | Kim Young Jun | 단계구분도 활성화 여부 상태 추가
    const [isChoroplethAnalysis, setIsChoroplethAnalysis] = useState(false);
    const [currentRegionCode, setCurrentRegionCode] = useState<string | null>(null);

    // gsshin (2026.1.16) : 카테고리 선택 비활성화 상태
    const [categorySelectDisabled, setCategorySelectDisabled] = useState(false);
    // 경사도 분석 hook 사용
    const slopeHook = useSlopeAnalysis();

    // 장소조회 reload
    const sidesheetPoiListRef = useRef<POIListViewRef>(null);
    const setAccState = (vehicle?: TransportMode, ready?: boolean) => {
        if (!vehicle && ready === undefined) {
            _setAccState({ ready: false });
            return;
        }

        const newAccState = accState ? { ...accState } : { ready: false };

        if (vehicle) {
            newAccState.vehicle = vehicle;
        }

        if (ready !== undefined) {
            newAccState.ready = ready;
        }

        _setAccState(newAccState);
    };

    const [isMobileView, setIsMobileView] = useState(false);

    // 반응형 MobileSize
    useEffect(() => {
        const checkViewport = () => {
            if (window.innerWidth <= 780) {
                setIsMobileView(true);
            } else {
                setIsMobileView(false);
            }
        };

        checkViewport();
        window.addEventListener("resize", checkViewport);
        return () => window.removeEventListener("resize", checkViewport);
    }, []);

    // Toast
    const [toast, setToast] = useState<ToastData | undefined>(); // [58]

    useEffect(() => {
        if (!baseMapID) movePage();

        loadMap(baseMapID!);

        props.setIsMap(true);
        document.addEventListener("keydown", escEvent);

        return () => {
            props.setIsMap(false);
            document.removeEventListener("keydown", escEvent);
        };
    }, []);

    // 카테고리 관리
    const [isCategoriesReady, setIsCategoriesReady] = useState(false); // [60]

    // 로그인 상태 체크 - useEffect보다 먼저 선언
    const isLogin = useLoginStatus(); // 로그인 방식 변경 | smyun | 250908

    // 25.09.02 | Kim Young Jun | 오버랩 활성화 로직 수정
    useEffect(() => {
        const fetchOverlapMapInfos = async () => {
            const overlapMapId = params.get("overlap");

            if (overlapMapId) {
                // 25.09.09 | 로그인 체크 - 오버랩 기능은 로그인 유저만 사용 가능
                if (!isLogin) {
                    // 오버랩 파라미터를 제거하고 기본 지도로 리다이렉트
                    movePage(`${location.pathname}?id=${baseMapID}`);
                    return;
                }

                const mapIds = overlapMapId.split(","); // ,를 찍어서 오버랩 추가
                const overlapMapInfos = await reqOverlapMapInfo(mapIds); // MapInfo 조회

                // 25.09.09 | URL 직접 접근 시 baseMapInfo가 없는 경우 기본 지도 정보 먼저 로드
                let baseMapInfo = getBaseMapInfo();

                if (!baseMapInfo && baseMapID) {
                    // console.log("@@@@@@@@ URL 직접 접근, 기본 지도 정보 로드 중...");
                    // console.log("     >>> baseMapID:", baseMapID);
                    // console.log("     >>> overlapIDs:", overlapMapId);
                    const baseMapResponse = await getMapInfo(baseMapID);

                    if (baseMapResponse.flag && baseMapResponse.result) {
                        baseMapInfo = baseMapResponse.result;
                        // console.log("@@@@@@@@ 기본 지도 정보 로드 완료:", baseMapInfo);
                    } else {
                        // console.error("@@@@@@@@ 기본 지도 정보 로드 실패");
                        // 기본 지도 정보를 가져올 수 없으면 기본 지도로 리다이렉트
                        movePage(`${location.pathname}?id=${baseMapID}`);
                        return;
                    }
                }

                console.log("[MapScreen] 오버랩 전환 최초 로직 실행");
                // console.log("@@@@@@@@ [MapScreen] baseMapInfo: ", baseMapInfo);

                if (baseMapInfo) {
                    // console.log("@@@@@@@@ [MapScreen] 순차 처리 시작 - 1단계: 기본지도 먼저 설정");

                    // 1. analysisXIDs 초기화 (먼저 처리)
                    if (analysisXIDs && analysisXIDs.length > 0) {
                        setAnalysisXIDs(undefined);
                    }

                    // 2. 기본 지도 정보만 먼저 설정 (오버랩 없이)
                    // console.log("@@@@@@@@ [MapScreen] 기본지도만 먼저 설정");
                    setMapInfos([baseMapInfo]);
                    setSelectedMapIdx(0); // 기본 지도 선택

                    // 3. 기본 지도 카테고리 설정
                    if (baseMapInfo.category) {
                        let categories = baseMapInfo.category;
                        if (categories.length > 0) {
                            categories = [defaultCategory, ...categories];
                        }
                        setCurrentCategories(categories);
                        setIsCategoriesReady(true);
                    }

                    // 4. 기본 지도 렌더링 완료 후 오버랩 추가 (순차적으로 하나씩)
                    setTimeout(() => {
                        // console.log("@@@@@@@@ [MapScreen] 순차 처리 - 2단계: 오버랩 지도 순차 추가 시작");
                        // console.log(`      >>> 총 ${overlapMapInfos.length}개의 오버랩 지도 순차 처리`);

                        // 25.09.09 | 오버랩 모드 활성화 - activeIdx를 Overlap으로 설정
                        setActiveIdx(MapToolType.Overlap);

                        // 오버랩 지도 추가 시 선택된 POI 즉시 리셋 (FloatingSideSheet 재열림 방지)
                        setSelectedPoiIdx(0);

                        // OverlapMapInfo를 MapInfo로 변환
                        const convertedOverlapMaps = overlapMapInfos.map(
                            (info) =>
                                ({
                                    ...info,
                                    bookmark: info.bookmark ?? false, // undefined인 경우 false로 변환
                                } as MapInfo)
                        );

                        // 25.09.02 | Kim Young Jun | 카테고리 배열 미리 확장
                        const newArray = [...selectedCategoryArray];
                        while (newArray.length <= overlapMapInfos.length) {
                            newArray.push(0);
                        }
                        setSelectedCategoryArray(newArray);

                        // 오버랩 지도들을 하나씩 순차적으로 추가하는 함수
                        const addOverlapMapSequentially = (index: number) => {
                            if (index >= convertedOverlapMaps.length) {
                                // console.log("@@@@@@@@ [MapScreen] 모든 오버랩 지도 추가 완료");
                                // 모든 오버랩 지도 추가 완료 후 마지막 지도 선택
                                const lastMapIdx = convertedOverlapMaps.length; // 기본 지도(0) + 오버랩 지도들
                                // console.log(`@@@@@@@@ [MapScreen] 최종 선택: 마지막 오버랩 지도 인덱스 ${lastMapIdx}`);
                                setSelectedMapIdx(lastMapIdx);
                                return;
                            }

                            const currentOverlapMap = convertedOverlapMaps[index];
                            // console.log(`@@@@@@@@ [MapScreen] 오버랩 지도 ${index + 1}/${convertedOverlapMaps.length} 추가: ${currentOverlapMap.title}`);

                            // 현재까지의 지도들 (기본 지도 + 현재까지의 오버랩 지도들)
                            const currentMapInfos = [baseMapInfo, ...convertedOverlapMaps.slice(0, index + 1)];
                            setMapInfos(currentMapInfos as MapInfo[]);

                            // 중간에는 selectedMapIdx 변경하지 않음 (불필요한 switchOverlap 호출 방지)
                            // 마지막에만 선택하여 올바른 투명도 설정

                            // 다음 오버랩 지도를 추가하기 위한 지연 (데이터 로딩과 레이어 생성 완료를 위해)
                            setTimeout(() => {
                                addOverlapMapSequentially(index + 1);
                            }, 400); // 각 오버랩 지도 추가 간격 (레이어 생성 시간 고려하여 조금 더 길게)
                        };

                        // 첫 번째 오버랩 지도부터 시작
                        addOverlapMapSequentially(0);
                    }, 100); // 기본 지도 렌더링 완료를 위한 초기 지연
                }
            } else {
                // 25.09.02 | Kim Young Jun | 오버랩 해제 시 즉시 기본 지도 상태 복원
                if (isOverlapModeActive()) {
                    // 25.09.09 | 오버랩 모드 해제 시 activeIdx 초기화
                    if (activeIdx === MapToolType.Overlap) {
                        setActiveIdx(MapToolType.None);
                    }

                    // 기본 지도의 카테고리 복원
                    const baseMapInfo = getBaseMapInfo();
                    if (baseMapInfo?.category) {
                        let _categories = baseMapInfo.category;
                        if (_categories.length > 0) {
                            _categories = [defaultCategory, ..._categories];
                        }
                        setCurrentCategories(_categories);
                    }

                    // 기본 지도의 저장된 카테고리 선택 상태 복원 (안전한 접근)
                    setTimeout(() => {
                        const currentArray = selectedCategoryArray || [];
                        const baseMapCategory = currentArray[0] || 0;
                        // 25.09.02 | Kim Young Jun | setCurrentSelectedCategory 사용
                        setCurrentSelectedCategory(baseMapCategory);
                    }, 50);

                    // 25.09.09 | 기본 콘텐츠로 전환
                    changeContent(ContentList.List);
                }

                // console.log("@@@@@@@@ [MapScreen] SelectedMapIdx 업데이트");
                // 기본 지도만 남김
                const baseMapInfo = getBaseMapInfo();
                if (baseMapInfo) {
                    setMapInfos([baseMapInfo]);
                }
                setSelectedMapIdx(0);

                // 25.09.02 | Kim Young Jun | POI 리스트 즉시 새로고침
                setTimeout(() => {
                    sidesheetPoiListRef.current?.reload();
                }, 100);
            }
        };

        fetchOverlapMapInfos();
    }, [params, isLogin, baseMapID]);

    // 오버랩 지도 상태 변경 감지 - 분석 툴 활성화 및 지도 레이어 업데이트
    useEffect(() => {
        const overlapMapsLength = getOverlapMapInfos().length;
        if (overlapMapsLength !== 0) {
            setActiveIdx(MapToolType.Overlap); // 주소로 접근 시
        }
    }, [mapInfos]);

    // mapInfos 변경 시 카테고리 배열 업데이트
    useEffect(() => {
        // console.log(`@@@@@@@@ [MapScreen] [CATEGORIES_ARRAY_UPDATE] mapInfos 변경 감지: mapInfos.length=${mapInfos.length}`);

        const newCategoriesArray: CategoryOptions[][] = [];

        mapInfos.forEach((mapInfo, index) => {
            let categories = mapInfo.category || [];

            // 카테고리가 1개 이상이면 "전체" 옵션 추가
            if (categories.length > 0) {
                categories = [defaultCategory, ...categories];
            } else if (categories.length === 0) {
                // 카테고리가 없으면 기본 "전체" 옵션만
                categories = [defaultCategory];
            }

            newCategoriesArray[index] = categories;
            // console.log(
            //     `@@@@@@@@ [MapScreen] [CATEGORIES_ARRAY_UPDATE] 지도 ${index} 카테고리 설정:`,
            //     categories.map((c) => c.label)
            // );
        });

        // 카테고리 배열이 비어있으면 기본값 설정
        if (newCategoriesArray.length === 0) {
            newCategoriesArray[0] = [defaultCategory];
            // console.log(`@@@@@@@@ [MapScreen] [CATEGORIES_ARRAY_UPDATE] 기본 카테고리 배열 설정`);
        }

        setCategoriesArray(newCategoriesArray);
        // console.log(`@@@@@@@@ [MapScreen] [CATEGORIES_ARRAY_UPDATE] 카테고리 배열 업데이트 완료`);
    }, [mapInfos]);

    useEffect(() => {
        setShowFloatingSheet(false); // 251029 | smyun | 로그인 상태 변경 시 floatingSideSheet 닫음
        setSelectedPoiIdx(0); // 선택된 마커 초기화
    }, [isLogin]);

    useEffect(() => {
        switch (sideSheetCont) {
            case ContentList.AccountUpgrade:
            case ContentList.AddMultiplePlaces:
            case ContentList.AddSinglePlace:
            case ContentList.EditPlace:
            case ContentList.AddPlace:
                setShowFloatingSheet(false);
                setShowTopFloatingButtons(false);
                break;
            case ContentList.Overlap:
            case ContentList.Direction:
            case ContentList.Accessibility:
                setShowFloatingSheet(false);
                break;
            default:
                // 오버랩 모드가 활성화된 경우 FloatingSideSheet 자동 재열림 방지
                selectedPoiIdx !== 0 && !isOverlapActive() && setShowFloatingSheet(true);
                setShowTopFloatingButtons(true);
                break;
        }

        switch (sideSheetCont) {
            case ContentList.AddMultiplePlaces:
            case ContentList.AddSinglePlace:
            case ContentList.EditPlace:
            case ContentList.AddPlace:
                setIsAnalysisMenuOpen(false);
                break;
            default:
        }
    }, [sideSheetCont]);

    useEffect(() => {
        setShowFloatingSheet(false);

        const prev = prevActiveIdxRef.current; // 이전에 선택한 분석도구
        // toolReset(true); // 분석 메트릭 selector (분석옵션)도 activeIdx로 관리중이므로 주석처리

        switch (activeIdx) {
            case MapToolType.Overlap:
                changeContent(ContentList.Overlap);
                break;
            case MapToolType.Direction:
                setLoadingMessage("시간별 이동거리를 분석중입니다.");
                changeContent(ContentList.Direction);
                break;
            case MapToolType.Accessibility:
                changeContent(ContentList.Accessibility);
                break;
            case MapToolType.Add:
                changeContent(ContentList.AddPlace);
                break;
            case MapToolType.None:
                changeContent(ContentList.List);
                // 오버랩 모드가 활성화되어 있고 None으로 전환하는 경우 오버랩 해제
                if (selectedMapIdx > 0) {
                    resetOverlap(true);
                }
                // 250909 | smyun | Direction -> None 으로 전환될 때 toolReset
                if (prev === MapToolType.Direction) {
                    toolReset(true);
                }
                break;
            default:
                changeContent(ContentList.List);
                break;
        }

        ![MapToolType.Overlap, MapToolType.Init].includes(activeIdx) && isOverlapActive() && resetOverlap(false);

        setAnalysisMetricsToolIdx(MapToolType.Place);
        setExtentToolIdx(MapToolType.DrawCustom);

        // gsshin ( 2026.01.16 ) 단계구분도, 차트, 버블뱁 활성화 시 카테고리 선택 비활성화 처리
        if(activeIdx === MapToolType.Choropleth || activeIdx === MapToolType.Chart || activeIdx === MapToolType.Bubble) {
            setCurrentSelectedCategory(0);
            setCategorySelectDisabled(true);
        }else{
            setCategorySelectDisabled(false);
        }

    }, [activeIdx]);

    // 단계구분도 / 히트맵 분석에서 장소 정보 획득 API 중복호출 제어
    useEffect(() => {
        if (activeIdx === MapToolType.Choropleth || activeIdx === MapToolType.Heat) {
            setIsLoading(true);
            setLoadingMessage("장소 정보를 불러오는 중입니다.");
            reqDetailFeatureData(baseMapID!);
        }
    }, [activeIdx, baseMapID]);

    // 장소 수정 시 맵 정보 새로고침
    useEffect(() => {
        if (!baseMapID || !editPlaceStatus) return;

        const refreshMapAndPOI = async () => {
            // console.log("@@@ 장소 수정 완료 - 맵 정보 새로고침 시작 @@@");

            // 1. 맵 정보 새로고침
            await loadMap(baseMapID);

            // 2. 잠시 대기 후 POI 리스트 새로고침
            setTimeout(() => {
                sidesheetPoiListRef.current?.reload();
                // console.log("@@@ POI 리스트 새로고침 실행 @@@");
            }, 1000); // 1초 동안 맵 데이터 로딩 완료 대기
        };

        refreshMapAndPOI();
    }, [editPlaceStatus]);

    // 사이드 레일 즐겨찾기 & 마커 상세정보에서 '도움돼요' 클릭 시 실행 | 2025.04.15 | kmy
    useEffect(() => {
        const fetchData = async () => {
            // 오버랩 지도에서 도움돼요 클릭 시
            if (selectedMapIdx > 0 && getOverlapMapInfos().length >= selectedMapIdx) {
                const overlap = params.get("overlap");
                if (overlap) {
                    const mapIds = overlap.split(",");
                    const _overlapMapInfos = await reqOverlapMapInfo(mapIds);
                    // 기존 기본 지도와 새로운 오버랩 지도들을 통합
                    const baseMapInfo = getBaseMapInfo();
                    if (baseMapInfo) {
                        const convertedOverlapMaps = _overlapMapInfos.map(
                            (info) =>
                                ({
                                    ...info,
                                    bookmark: info.bookmark ?? false,
                                } as MapInfo)
                        );
                        setMapInfos([baseMapInfo, ...convertedOverlapMaps]);
                    }
                }
            } else {
                // 기존 지도에서 클릭 시
                const response = await getMapInfo(baseMapID!);
                if (response.flag) {
                    // 기본 지도 정보 업데이트
                    const updatedBaseMap = response.result;
                    const overlapMaps = getOverlapMapInfos();
                    setMapInfos([updatedBaseMap, ...overlapMaps]);
                }
            }
        };

        fetchData();
    }, [thumbState, isBookmark]);

    /* ======================================= 사이드시트 관리 영역 ======================================= */
    const [shouldSideRender, setShouldSideRender] = useState(true); // display 여부 제어용 상태
    useEffect(() => {
        if (!showSideSheet) {
            // 사라지는 트랜지션 후 display: none
            const timer = setTimeout(() => {
                setShouldSideRender(false);
            }, 400); // 0.4초 후 none 처리

            return () => clearTimeout(timer);
        } else {
            // 다시 나타나야 할 때 display 복구
            setShouldSideRender(true);
        }
    }, [showSideSheet]);

    const [shouldFloatingRender, setShouldFloatingRender] = useState(true); // display 여부 제어용 상태
    useEffect(() => {
        if (!showFloatingSheet) {
            // 사라지는 트랜지션 후 display: none
            const timer = setTimeout(() => {
                setShouldFloatingRender(false);
            }, 200); // 0.20초 후 none 처리

            return () => clearTimeout(timer);
        } else {
            // 다시 나타나야 할 때 display 복구
            setShouldFloatingRender(true);
        }
    }, [showFloatingSheet]);

    // 25.07.11 | Kim Young Jun | 카테고리 컨테이너 너비 관련 상태와 ref 추가
    const categoryContainerRef = useRef<HTMLDivElement | null>(null);
    const [categoryContainerWidth, setCategoryContainerWidth] = useState<number>(100); // 기본값 100으로 설정

    /* sideSheet & floating 영역 너비 감지 + 카테고리 영역 설정 - 통합 | 2025.09.02 | smyun ----------------------- */
    // 1) 너비 측정 함수
    // 선택된 마커만 해제 (FloatingSideSheet 유지) - POI 선택시 사용
    const clearSelectedMarkerOnly = useCallback(() => {
        if (clearSelectedMarkerOnlyRef.current) {
            clearSelectedMarkerOnlyRef.current();
        }
    }, []);

    // 선택된 마커 해제 + FloatingSideSheet 닫기 - 카테고리 변경시 사용
    const clearSelectedMarkerAndCloseSheet = useCallback(() => {
        if (clearSelectedMarkerAndCloseSheetRef.current) {
            clearSelectedMarkerAndCloseSheetRef.current();
        }
    }, []);

    const measure = useCallback(() => {
        // 트랜지션 고려하여 다음 페인트 직전으로 살짝 미루어 레이아웃 안정 보장
        requestAnimationFrame(() => {
            // side영역 확인 및 설정
            const side = sideRef.current; // sideRail+sideSheet DIV
            const floating = floatingSideRef.current;

            const sideWidth = side?.getBoundingClientRect().width ?? 0;
            const floatingWidth = floating?.getBoundingClientRect().width ?? 0;

            const total = showSideSheet && showFloatingSheet ? sideWidth + floatingWidth : sideWidth;

            setSideSheetWidth(total);

            // 카테고리 영역 설정
            const vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
            setCategoryContainerWidth(Math.max(0, vw - total));

            // 디버깅 로그
            // console.log("[MEASURE] side:", sideWidth, "floating:", floatingWidth, "total:", total, "vw:", vw);
        });
    }, [showSideSheet, showFloatingSheet]);

    // 2) 최초/토글 시 재측정 (레이아웃 직후)
    useLayoutEffect(() => {
        measure();
    }, [measure, shouldSideRender, shouldFloatingRender]);

    // 3) 리사이즈에 반응 (디바운스 필요하면 requestAnimationFrame 안에만 두면 충분)
    useEffect(() => {
        const onResize = () => measure();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [measure]);

    // 4) 사이드 DOM 자체의 크기 변화를 추적 (미디어쿼리/폰트/스크롤바 등 모든 원인)
    useEffect(() => {
        if (typeof ResizeObserver === "undefined") return; // 구형 브라우저 가드

        const ro = new ResizeObserver(() => measure());

        const side = sideRef.current;
        const floating = floatingSideRef.current;
        if (side) ro.observe(side);
        if (floating) ro.observe(floating);

        // sideSheet가 unmount되었다가 다시 mount될 수 있으면, 상위 컨테이너도 observe 고려
        return () => ro.disconnect();
    }, [measure]);

    // 5) 미디어쿼리 변경 감지 (일부 레이아웃은 리사이즈 이벤트 없이도 MQL만 바뀜)
    useEffect(() => {
        const mql1440 = window.matchMedia("(max-width: 1440px)");
        const mql780 = window.matchMedia("(max-width: 780px)");

        const handler = () => measure();

        // 최신 스펙: addEventListener('change', ...)
        mql1440.addEventListener?.("change", handler);
        mql780.addEventListener?.("change", handler);

        // 구형 브라우저 대응
        mql1440.addListener?.(handler);
        mql780.addListener?.(handler);

        return () => {
            mql1440.removeEventListener?.("change", handler);
            mql780.removeEventListener?.("change", handler);
            mql1440.removeListener?.(handler);
            mql780.removeListener?.(handler);
        };
    }, [measure]);

    /* -------------------- sideSheet & floating 영역 너비 감지 + 카테고리 영역 설정 - 통합 <끝> ------------------------------- */

    // 25.09.02 | Kim Young Jun | selectedCategoryArray만 사용하도록 헬퍼 함수들 추가
    const [selectedCategoryArray, setSelectedCategoryArray] = useState<number[]>([0]); // 기본 지도 카테고리 0으로 초기화
    const [forceUpdate, setForceUpdate] = useState(0);

    // mapInfos 통합 관리를 위한 헬퍼 함수들 - useMemo로 최적화
    //현재 선택된 지도 정보 조회 함수 - mapInfos 배열의 현재 인덱스 요소 반환
    const getCurrentMapInfo = useMemo((): MapInfo | undefined => {
        return mapInfos[selectedMapIdx];
    }, [mapInfos, selectedMapIdx]);

    // 기준 지도 정보 조회 함수 - mapInfos 배열의 첫 번째 요소 반환
    const getBaseMapInfo = (): MapInfo | undefined => {
        return mapInfos[0];
    };

    const getOverlapMapInfos = (): MapInfo[] => {
        return mapInfos.slice(1);
    };

    const isOverlapModeActive = (): boolean => {
        return mapInfos.length > 1;
    };

    // URL 기반 오버랩 모드 체크
    const isOverlapModeURL = (): boolean => {
        return params.get("overlap") !== null;
    };

    // 현재 선택된 지도의 카테고리 배열 getter 함수
    const getCurrentCategories = (): CategoryOptions[] => {
        return categoriesArray[selectedMapIdx] || [{ label: "전체", disabled: false }];
    };

    // 현재 선택된 지도의 카테고리 배열 setter 함수
    const setCurrentCategories = (categories: CategoryOptions[]) => {
        const newCategoriesArray = [...categoriesArray];
        while (newCategoriesArray.length <= selectedMapIdx) {
            newCategoriesArray.push([{ label: "전체", disabled: false }]);
        }
        newCategoriesArray[selectedMapIdx] = categories;
        setCategoriesArray(newCategoriesArray);
        // console.log(`@@@@@@@@ [MapScreen] setCurrentCategories() 지도 ${selectedMapIdx}의 카테고리 설정:`, categories);
    };

    // 25.09.02 | Kim Young Jun | 현재 선택된 카테고리 getter 함수
    const getCurrentSelectedCategory = (): number => {
        return selectedCategoryArray[selectedMapIdx] || 0;
    };

    // 25.09.02 | Kim Young Jun | 현재 선택된 카테고리 setter 함수
    const setCurrentSelectedCategory = (value: React.SetStateAction<number>) => {
        const categoryIndex = typeof value === "function" ? value(getCurrentSelectedCategory()) : value;
        const newArray = [...selectedCategoryArray];
        while (newArray.length <= selectedMapIdx) {
            newArray.push(0);
        }
        newArray[selectedMapIdx] = categoryIndex;
        setSelectedCategoryArray(newArray);
        // console.log(`[CATEGORY_SET] 지도 ${selectedMapIdx}의 카테고리를 ${categoryIndex}로 설정: [${newArray.join(", ")}]`);
    };

    // 25.09.02 | Kim Young Jun | 오버랩 지도 로드 시 카테고리 배열 초기화 개선
    useEffect(() => {
        const overlapMaps = getOverlapMapInfos();
        // console.log("@@@@ useEffect [getOverlapMapInfos().length]: ", overlapMaps.length);

        if (overlapMaps.length === 0) {
            // 오버랩이 해제되면 기본 지도만 남김
            if (selectedCategoryArray.length > 1) {
                const baseCategory = selectedCategoryArray[0] || 0;
                setSelectedCategoryArray([baseCategory]);
                // console.log(`[OVERLAP_CLEAR] 오버랩 해제 - 기본 지도 카테고리 ${baseCategory}만 유지`);
            }
            return;
        }

        const totalMaps = overlapMaps.length + 1; // 기본 지도 + 오버랩 지도들
        const initialArray = new Array(totalMaps).fill(0);

        // 25.09.02 | Kim Young Jun | 기본 지도는 현재 선택된 카테고리 유지, 새 오버랩 지도는 0으로 초기화
        if (selectedCategoryArray.length === 0) {
            // 최초 초기화 시
            initialArray[0] = 0; // 기본 지도 카테고리 0
            // console.log(`[OVERLAP_INIT] 최초 초기화: [${initialArray.join(", ")}]`);
        } else {
            // 기존 배열이 있는 경우 기본 지도와 기존 오버랩 지도들 보존
            for (let i = 0; i < Math.min(selectedCategoryArray.length, initialArray.length); i++) {
                initialArray[i] = selectedCategoryArray[i];
            }
            // 새로 추가된 오버랩 지도들은 0으로 유지 (이미 fill(0)으로 초기화됨)
            // console.log(`[OVERLAP_EXPAND] 배열 확장: [${initialArray.join(", ")}]`);
        }

        setSelectedCategoryArray(initialArray);
    }, [mapInfos.length]);

    // 25.09.02 | Kim Young Jun | 지도 스위치 시 카테고리 변화 및 상태 복원 함수 개선
    useEffect(() => {
        let _categories: CategoryOptions[] = [];

        // console.log(`@@@@@@@@ [MapScreen] [MAP_SWITCH] 지도 전환 - selectedMapIdx: ${selectedMapIdx}`);
        // console.log(`@@@@@@@@ [MapScreen] [MAP_SWITCH] 현재 selectedCategoryArray: [${selectedCategoryArray.join(", ")}]`);

        // 25.09.02 | Kim Young Jun | 배열 크기 확장이 필요한 경우
        const newArray = [...selectedCategoryArray];
        while (newArray.length <= selectedMapIdx) {
            newArray.push(0);
        }

        // 이전 지도의 현재 선택 상태를 저장하는 로직 개선
        const currentIndex = selectedMapIdx;
        const prevIndex = prevSelectedCategoryRef.current;

        // 이전 선택 인덱스가 유효하고 현재와 다른 경우에만 저장
        if (prevIndex !== null && prevIndex !== currentIndex && prevIndex < newArray.length) {
            // 25.09.02 | Kim Young Jun | 이전 지도의 현재 카테고리 상태를 저장
            const prevCategory = selectedCategoryArray[prevIndex] || 0;
            newArray[prevIndex] = prevCategory;
            // console.log(`@@@@@@@@ [MapScreen] [MAP_SWITCH] 이전 지도 ${prevIndex} 상태 저장: ${prevCategory}`);
        }

        // 현재 선택된 인덱스를 이전 값으로 저장
        prevSelectedCategoryRef.current = selectedMapIdx;

        // 25.09.02 | Kim Young Jun | FloatingSideSheet가 열려있지 않을 때만 선택된 마커 초기화
        if (!showFloatingSheet || selectedPoiIdx === 0) {
            setSelectedPoiIdx(0); // 선택된 마커 초기화
        }

        // 카테고리 정보 가져오기
        if (selectedMapIdx === 0) {
            const baseMapInfo = getBaseMapInfo();
            if (!baseMapInfo) return;
            _categories = baseMapInfo.category;
        } else {
            const overlapMaps = getOverlapMapInfos();
            if (!overlapMaps[selectedMapIdx - 1]) return;
            _categories = overlapMaps[selectedMapIdx - 1].category;
        }

        if (_categories.length > 0) {
            _categories = [defaultCategory, ..._categories];
        }

        // 25.09.02 | Kim Young Jun | 전환할 지도의 저장된 카테고리 상태 복원 및 범위 체크
        const savedCategory = newArray[selectedMapIdx] || 0;
        console.log(
            `@@@@@@@@ [MapScreen] [MAP_SWITCH] 지도 ${selectedMapIdx}로 전환, 저장된 카테고리: ${savedCategory}, 새 카테고리 길이: ${_categories.length}`
        );

        // 저장된 카테고리가 새 카테고리 범위를 벗어나면 0으로 리셋
        if (savedCategory >= _categories.length) {
            newArray[selectedMapIdx] = 0;
            // console.log(`@@@@@@@@ [MapScreen] [MAP_SWITCH] 카테고리 범위 초과로 0으로 리셋: ${savedCategory} → 0`);
        }

        // 25.09.02 | Kim Young Jun | 카테고리 설정과 배열 업데이트를 동시에 수행
        if (_categories.length) {
            // console.log(`@@@@@@@@ [MapScreen] [MAP_SWITCH] 카테고리 및 배열 동시 업데이트`);

            // 상태 업데이트를 하나의 배치로 처리
            setCurrentCategories(_categories);

            // selectedCategoryArray 업데이트 (React 18의 배치 업데이트로 함께 처리됨)
            if (JSON.stringify(newArray) !== JSON.stringify(selectedCategoryArray)) {
                // console.log(`@@@@@@@@ [MapScreen] [MAP_SWITCH] 배열 업데이트: [${selectedCategoryArray.join(", ")}] → [${newArray.join(", ")}]`);
                setSelectedCategoryArray(newArray);
            }
        }

        // 25.09.02 | Kim Young Jun | POI 리스트 새로고침 제거 - POIListView useEffect에서 자동 처리됨
        // CategoryGoup에서 selectedCategoryArray 업데이트 시 POIListView가 자동으로 감지하여 새로고침
    }, [selectedMapIdx, mapInfos.length]);

    // 25.09.02 | Kim Young Jun | selectedCategory 동기화 useEffect 제거 (더 이상 필요없음)
    // selectedCategoryArray만 사용하므로 별도 동기화 불필요

    // useEffect(() => {
    //     // 분석결과 xid의 값이 set 되면 해당 xid로 목록 조회
    //     // changeContent()에서 State인 setSideSheetCont실행 => 렌더링 => POIListView를 return
    //     if (analysisXIDs && analysisXIDs.length > 0) {
    //         // 범위분석에서 다른 분석도구로 전환하는 경우가 아닐 때만 List로 변경
    //         // 시간별 이동거리나 접근성 분석이 활성화된 상태에서는 해당 패널 유지
    //         const shouldKeepCurrentContent = [
    //             MapToolType.Direction,
    //             MapToolType.Accessibility,
    //             MapToolType.Overlap
    //         ].includes(activeIdx);

    //         if (!shouldKeepCurrentContent) {
    //             changeContent(ContentList.List);
    //         }
    //     }
    // }, [analysisXIDs, activeIdx]); // activeIdx 의존성 추가

    // 분석 결과 XID 변경 감지 - 분석 결과 목록 조회 및 업데이트
    useEffect(() => {
        if (analysisXIDs && analysisXIDs.length > 0) {
            // 분석결과 xid의 값이 set 되면 해당 xid로 목록 조회
            // changeContent()에서 State인 setSideSheetCont실행 => 렌더링 => POIListView를 return
            console.log("analysisXIDs changed: ", analysisXIDs);

            // 25.09.15 | 오버랩 콘텐츠가 표시되어야 할 때는 강제로 List로 변경하지 않음
            // 오버랩 모드가 활성화되어 있고 현재 activeIdx가 Overlap인 경우 변경하지 않음
            // 25.10.15 | 장소 추가 모드일 때도 변경하지 않음 (AddPlace 콘텐츠 유지)
            if (
                activeIdx === MapToolType.Overlap ||
                activeIdx === MapToolType.Direction ||
                activeIdx === MapToolType.Accessibility ||
                activeIdx === MapToolType.Add
            )
                return;

            changeContent(ContentList.List);
        }
    }, [analysisXIDs, activeIdx]);

    useEffect(() => {
        const items = extractDropdownSelectorItems(allPlaceData);
        if (dropdownSelectorItems.length === 0 && items.length > 0) {
            setDropdownSelectorItems(items);
        }
    }, [allPlaceData]);

    // 접근성 분석 관련
    useEffect(() => {
        // 접근성 분석 초기화 시 실행함수
        resetAcc && changeContent(ContentList.Accessibility);
        _setAccState({ ready: false });
    }, [resetAcc]);

    const [agreeNoneAddress, setAgreeNoneAddress] = useState<boolean>(false);
    const [userCustomAddress, setUserCustomAddress] = useState("");
    const userCustomAddressRef = useRef("");

    useEffect(() => {
        userCustomAddressRef.current = userCustomAddress;
    }, [userCustomAddress]);

    // 미등록 주소지
    // useEffect(() => {
    //     console.log("@@ [MapScreen] agreeNoneAddress: ", agreeNoneAddress);
    // }, [agreeNoneAddress]);

    // 주소 찾기 실패 시 모달 팝업
    useEffect(() => {
        if (isOpenAgreeModal) {
            userCustomAddressRef.current = "";
            openModal({
                title: "안내",
                children: (
                    <>
                        주소를 찾을 수 없습니다.
                        <br />
                        현재 위치의 주소를 입력해 주세요.
                    </>
                ),
                modalInput: (
                    <ModalInput
                        placeholder="주소를 입력해주세요."
                        value={userCustomAddressRef.current}
                        onChange={setUserCustomAddress} // <- setState를 직접 넘김
                    />
                ),
                modalButtons: () => (
                    <ModalButtons
                        buttonCount={2}
                        buttons={[
                            {
                                label: "등록",
                                color: "Primary",
                                disabled: userCustomAddressRef.current.trim().length === 0,
                                onClick: () => {
                                    console.log("입력된 주소:", userCustomAddress);
                                    setNewPlaceAddress(userCustomAddressRef.current);
                                    setAgreeNoneAddress(true);
                                    setIsOpenAgreeModal(false);
                                    closeModal();
                                },
                            },
                            {
                                label: "취소",
                                color: "Secondary",
                                onClick: () => {
                                    setAgreeNoneAddress(false);
                                    setIsOpenAgreeModal(false);
                                    closeModal();
                                },
                            },
                        ]}
                    />
                ),
                onAfterClose: () => {
                    setIsOpenAgreeModal(false);
                    setUserCustomAddress("");
                    userCustomAddressRef.current = "";
                },
            });
        }
    }, [isOpenAgreeModal]);

    // 25.07.11 | Kim Young Jun | isAnalysisActive 분석상태 체크 callback 함수 정의
    const isAnalysisActive = useCallback(() => {
        return [
            MapToolType.Region,
            MapToolType.Choropleth,
            MapToolType.Chart,
            MapToolType.Bubble,
            MapToolType.Heat,
            MapToolType.Direction,
            MapToolType.Accessibility,
            MapToolType.ExtentAnalysis,
            MapToolType.Overlap,
            MapToolType.Slope,
        ].includes(activeIdx);
    }, [activeIdx]);

    const isNotUseMarkerAnalysis = useCallback(() => {
        return [MapToolType.Region, MapToolType.Choropleth, MapToolType.Chart, MapToolType.Bubble, MapToolType.Heat].includes(activeIdx);
    }, [activeIdx]);

    // 25.09.02 | Kim Young Jun | 오버랩 기능 활성화 상태 체크 함수 수정 - 통합된 상태 관리
    const isOverlapActive = useCallback(() => {
        return params.get("overlap") !== null && mapInfos.length > 1;
    }, [params, mapInfos.length]);

    // 25.07.18 | Kim Young Jun | 분석도구 이전 상태 추적
    const [prevAnalysisState, setPrevAnalysisState] = useState(false);
    const isFirstRender = useRef(true);

    // 분석도구 상태 변화 감지
    useEffect(() => {
        const currentAnalysisState = isAnalysisActive();

        if (!isFirstRender.current) {
            // 분석도구가 켜졌다가 꺼진 경우 (true -> false)
            if (prevAnalysisState && !currentAnalysisState) {
                console.log("[DEBUG] 분석도구가 비활성화됨");
            }
            // 분석도구가 꺼져있다가 켜진 경우 (false -> true)
            else if (!prevAnalysisState && currentAnalysisState) {
                console.log("[DEBUG] 분석도구가 활성화됨");
            }

            setPrevAnalysisState(!currentAnalysisState);
        } else {
            // 최초 렌더링 후 플래그 업데이트
            isFirstRender.current = false;
        }
    }, [isAnalysisActive(), prevAnalysisState]);

    // 25.07.07 | Kim Young Jun | Analysis Metric 트리거 함수 정의
    const triggerAnalysis = useCallback(() => {
        setAnalysisTriggerId((prev) => {
            reqDetailFeatureData(baseMapID!); // 장소 정보 로드

            return prev + 1;
        });
    }, []);

    // 25.08.04 | Kim Seong Min | 경사도 표출을 위한 최소 줌 레벨 변수 정의
    const minSlopeZoomLv = 10;

    // move page
    const movePage = (url?: To, option?: NavigateOptions) => {
        navigate(url ?? path.PUBLIC_MAPLIST, option);
    };

    // request data -------------------------
    const getMapInfo = async (mapid: string) => {
        return await DataProvider.GetMapInfo({ mapid });
    };

    const loadMap = async (mapid: string) => {
        // update view count
        await DataProvider.UpdateMapViewCnt({ mapid });

        // request Map Info
        const response = await getMapInfo(mapid);

        if (response.flag && response.result) {
            // 기본 지도 정보를 mapInfos 배열의 첫 번째 요소로 설정
            const baseMapInfo = response.result;
            const overlapMaps = getOverlapMapInfos();
            setMapInfos([baseMapInfo, ...overlapMaps]);
        } else {
            // 존재하지 않는 지도인 경우 목록 화면으로 이동
            if (response.status && response.status === 404) {
                movePage(undefined, { replace: true });
            }
            return; // response.result가 없으면 여기서 종료
        }

        let category = (response.result?.category as CategoryOptions[]) || [];

        if (category.length >= 1) {
            // console.log("@@ [MapScreen] setCurrentCategories category.length > 1: ", category);
            setCurrentCategories([defaultCategory, ...category]);
        } else if (category.length === 1) {
            // console.log("@@ [MapScreen] setCurrentCategories category.length === 1: ", category);
            setCurrentCategories([...category]);
        }

        setIsCategoriesReady(true);
    };

    const reqDetailFeatureData = async (mapid: string) => {
        try {
            const response = await DataProvider.GetFeatureList({ mapid }, { detail: true });
            if (!response.flag || !response.result) {
                console.error("GetFeatureListWithDetail 실패:", response);
                return;
            }

            // FeatureCollection 형태의 GeoJSON 데이터
            const featureCollection = response.result;

            if (featureCollection.features && Array.isArray(featureCollection.features)) {
                // GeoJSON Features를 AllPlaceData 형식으로 변환
                const tmpAllPlaceData: AllPlaceData = {};

                featureCollection.features.forEach((feature: any, index: number) => {
                    // Feature ID에서 XID 추출 (예: "k404dcc91b2aeaa7caa47487d1483e48a.148" -> 148)
                    const idParts = feature.id.split(".");
                    const xid = parseInt(idParts[idParts.length - 1]);

                    if (!isNaN(xid)) {
                        // 알파벳 필드만 한글 필드명으로 변환
                        const mappedProperties: any = {};

                        // FIELDNAME 매핑이 있는 경우 한글 필드명으로 변환
                        if (feature.properties.FIELDNAME) {
                            const fieldNameMap = feature.properties.FIELDNAME;

                            // 각 알파벳 키를 한글 필드명으로 매핑 (알파벳 필드만)
                            Object.keys(fieldNameMap).forEach((key) => {
                                if (key !== "FIELDNAME" && feature.properties[key] !== undefined) {
                                    const koreanFieldName = fieldNameMap[key];
                                    mappedProperties[koreanFieldName] = feature.properties[key];
                                }
                            });
                        }

                        tmpAllPlaceData[xid] = {
                            primaryinfo: {
                                viewCnt: feature.properties.VIEW_CNT ? parseInt(feature.properties.VIEW_CNT) : 0,
                                thumbCnt: feature.properties.THUMB_CNT ? parseInt(feature.properties.THUMB_CNT) : 0,
                                // storyCnt: feature.properties.STORY_CNT ? parseInt(feature.properties.STORY_CNT) : 0
                            },
                            placeInfo: mappedProperties,
                        };
                    }
                });
                setAllPlaceData(tmpAllPlaceData);
                return featureCollection;
            }
        } catch (error) {
            console.error("상세 피처 조회 실패:", error);
        }
    };

    // 오버랩 지도 정보 비동기 조회 함수 - 여러 지도ID로 MapInfo 일괄 요청
    const reqOverlapMapInfo = async (mapIds: string[]) => {
        const _overlapMapInfos: OverlapMapInfo[] = [];

        for (const mapid of mapIds) {
            try {
                // console.log("Requesting mapInfo for overlap mapid:", mapid);
                const response = await DataProvider.GetMapInfo({ mapid });

                if (!response.flag || !response.result) {
                    setActiveIdx(MapToolType.None);
                    continue;
                }

                _overlapMapInfos.push({ ...response.result, baseMapID: mapid });
            } catch (error) {
                // console.error("mapInfo 요청 실패:", error);
                setActiveIdx(MapToolType.None);
            }
        }

        return _overlapMapInfos;
    };

    const toolReset = (reset: boolean) => {
        console.log("toolreset");
        if (reset) {
            // 사이드 시트 내 항목 변경 시마다 비워져야 하는 state 초기화
            setSelectedNewPlace(undefined);
            if (!isExtentAnalysis) {
                // 25.09.02 | Kim Young Jun | setCurrentSelectedCategory 사용
                setCurrentSelectedCategory(0);
            }
            setNewPlaceCoords([]);
            setGeocodingState(-1);
            setAnalysisXIDs(undefined);
            setDirectionStartPoint(undefined);
            setIsochronePolygon(undefined);
            setAccState();
            setSelectedMapIdx(0); // 지도 선택 초기화 (기준지도로 돌아감)
            setShowFloatingSheet(false); // 플로팅사이드시트 닫기
            sideSheetCont !== ContentList.AddSinglePlace && setSelectedPoiIdx(0);
        }
    };

    // 사이드시트 콘텐츠 변경 함수 - 분석 툴 상태 관리 및 리셋
    const changeContent = (type: ContentList, reset?: boolean) => {
        reset && toolReset(reset); // 분석도구 초기화
        // 취소 버튼 클릭 시 분석도구 None 선택
        // console.log("[DBG] changeContent type: ", type, " reset: ", reset);
        if (reset) {
            if (activeIdx !== MapToolType.None && sideSheetCont !== ContentList.List) {
                setActiveIdx(MapToolType.None);
                return;
            }
        }

        if (type === ContentList.Back) {
            setSideSheetCont(prevSideSheetCont);
            setPrevSideSheetCont(type);
        } else if (type === ContentList.Overlap) {
            if (getOverlapMapInfos().length === 0) {
                // 오버랩 툴 버튼 클릭 시
                setPrevSideSheetCont(sideSheetCont);
                setSideSheetCont(type);
            }
        } else {
            setPrevSideSheetCont(sideSheetCont);
            setSideSheetCont(type);
        }
    };

    // 맵 플로팅 버튼 관련 함수 --------------------
    /**
     * 지도 선택 함수 (기존 selectOverlapMap에서 selectMap으로 변경)
     * @author mjkang
     * @param idx - 선택할 지도 인덱스 (0: 기준지도, 1+: 오버랩지도들)
     */
    const selectMap = (idx: number) => {
        setSelectedMapIdx(idx);
    };

    const resetOverlap = (forceResetActiveIdx: boolean = false) => {
        // 25.09.08 | Kim Young Jun | 경사도 분석 상태 보존
        const wasaSlopeActive = slopeHook.isSlopeActive;

        // 25.09.02 | Kim Young Jun | 오버랩 해제 시 기본 지도 상태 복원 - selectedCategoryArray만 사용
        // 현재 오버랩 상태를 기본 지도(인덱스 0)에 저장
        if (selectedMapIdx > 0) {
            const newArray = [...selectedCategoryArray];
            // 25.09.02 | Kim Young Jun | 현재 선택된 카테고리를 배열에서 가져와서 저장
            const currentCategory = selectedCategoryArray[selectedMapIdx] || 0;
            newArray[selectedMapIdx] = currentCategory;
            setSelectedCategoryArray(newArray);

            // 기본 지도의 저장된 카테고리 상태 복원
            const baseMapCategory = newArray[0] || 0;
            // 25.09.02 | Kim Young Jun | setCurrentSelectedCategory 사용하지 않고 직접 기본 지도로 설정
            const baseArray = [baseMapCategory];
            setSelectedCategoryArray(baseArray);
        }

        // 25.09.03 | Kim Young Jun | 오버랩 모드 해제 시 관련 상태값들 초기화

        // mapInfos를 기본 지도만 남기고 초기화
        if (mapInfos.length > 1) {
            const baseMapInfo = mapInfos[0]; // 기본 지도만 유지
            setMapInfos([baseMapInfo]);
        }

        // categoriesArray를 기본 지도 카테고리만 남기고 초기화
        if (categoriesArray.length > 1) {
            const baseCategoriesArray = categoriesArray[0] || [{ label: "전체", disabled: false }];
            setCategoriesArray([baseCategoriesArray]);
        }

        // selectedCategoryArray를 기본 지도 인덱스만 남기고 초기화
        const baseSelectedCategory = selectedCategoryArray[0] || 0;
        setSelectedCategoryArray([baseSelectedCategory]);

        // selectedMapIdx를 기본 지도(0)로 초기화
        setSelectedMapIdx(0);

        // 분석 결과 XID 초기화
        if (analysisXIDs && analysisXIDs.length > 0) {
            setAnalysisXIDs(undefined);
        }

        // 25.09.08 | Kim Young Jun | 경사도 분석이 활성화되어 있었다면 activeIdx를 Slope로 설정
        if (wasaSlopeActive) {
            setActiveIdx(MapToolType.Slope);
        }
        // 25.09.02 | Kim Young Jun | forceResetActiveIdx가 true이거나 현재 activeIdx가 Overlap일 때만 None으로 설정
        else if (forceResetActiveIdx || activeIdx === MapToolType.Overlap) {
            setActiveIdx(MapToolType.None);
        }

        // 사이드 시트 컴포넌트 전환 외에 종료 시 location reset 필요
        movePage(`${location.pathname}?id=${baseMapID}`); // reset location
    };

    // 맵 플로팅 버튼 관련 변수 --------------------
    const analysisToolOptions: MapToolOptions = {
        [MapToolType.Region]: {
            icon: "ctprvn",
            label: "행정경계",
        },
        [MapToolType.Choropleth]: {
            icon: "choropleth",
            label: "단계 구분도",
        },
        [MapToolType.Chart]: {
            icon: "chart",
            label: "차트",
        },
        [MapToolType.Bubble]: {
            icon: "bubble",
            label: "버블맵",
        },
        [MapToolType.Heat]: {
            icon: "heat",
            label: "히트맵",
        },
        [MapToolType.Direction]: {
            icon: "direction",
            label: "시간별 이동거리",
        },
        [MapToolType.Accessibility]: {
            icon: "target",
            label: "접근성 분석",
        },
        [MapToolType.ExtentAnalysis]: {
            icon: "extentAnalysis",
            label: "범위분석",
        },
        [MapToolType.Overlap]: {
            icon: "overlap",
            label: "오버랩",
        },
        [MapToolType.Slope]: {
            icon: "slope",
            label: "경사도",
        },
    };

    const analysisMetricOptions: MapToolOptions = {
        [MapToolType.Place]: {
            icon: "markerSelect2",
            label: "장소 개수",
        },
        [MapToolType.View]: {
            icon: "view",
            label: "조회수",
        },
        [MapToolType.ThumbUp]: {
            icon: "thumbUp",
            label: "도움돼요",
        },
        // [MapToolType.Story]: {
        //     icon: "chat",
        //     label: "이야기",
        // },
    };

    const extentToolOptions: MapToolOptions = {
        [MapToolType.DrawCustom]: {
            icon: "extentAnalysis",
            label: "직접 그리기",
        },
        [MapToolType.DrawCircle]: {
            icon: "circle",
            label: "원형 그리기",
        },
    };

    const measureToolOptions: MapToolOptions = {
        [MapToolType.Distance]: {
            icon: "distance",
            label: "거리",
        },
        [MapToolType.Area]: {
            icon: "extent",
            label: "면적",
        },
        [MapToolType.Radius]: {
            icon: "circle",
            label: "반경",
        },
    };

    const additionalToolOptions: MapToolOptions = {
        [MapToolType.Add]: {
            icon: "addPlace",
            label: "장소추가",
            disabled: isAnalysisActive(), // 25.07.11 | Kim Young Jun | 분석도구 활성화 시 장소추가 비활성화
        },
    };

    // geolocation, 확대, 축소 버튼
    const mapToolOptions = {
        gps: {
            icon: "gps",
            isLoading: isGPSLoading,
            onClick: () => {
                setIsGPSOn(true);
            },
        },
        zoom: [
            {
                icon: "plus",
                onClick: () => {
                    // console.log("[DBG] MapScreen mapToolOptions: " + zoomLv);
                    currentMap &&
                        currentMap.getView().animate({
                            zoom: zoomLv + 1,
                            duration: 500,
                        });
                },
            },
            {
                icon: "minus",
                onClick: () => {
                    currentMap &&
                        currentMap.getView().animate({
                            zoom: zoomLv - 1,
                            duration: 500,
                        });
                },
            },
        ],
    };

    /**
     * 지오코딩 진행도(%) getter/setter 통합 함수
     * @param num - {number} 현재 진행도
     * @returns geocodingState
     */
    const progress = (num?: number) => {
        if (num) {
            setGeocodingState(num);
            return num;
        } else {
            return geocodingState;
        }
    };

    /**
     * 신규 장소 등록(단일)/장소 수정 취소/완료 함수
     * @param idx? 선택된 장소 인덱스
     */
    const closeForm = (idx?: number) => {
        // 장소 수정 완료인지 확인
        const isEditComplete = sideSheetCont === ContentList.EditPlace && idx !== undefined && idx !== null && !isNaN(idx);

        // 장소 추가 완료인지 확인
        const isAddComplete = sideSheetCont === ContentList.AddSinglePlace && idx !== undefined && idx !== null && !isNaN(idx);

        if (isEditComplete) {
            changeContent(ContentList.List);
            setSelectedPoiIdx(idx);
            deleteNewPlace();
            return;
        }

        if (isAddComplete) {
            setActiveIdx(MapToolType.None);
            changeContent(ContentList.List);
            setSelectedPoiIdx(idx);
            deleteNewPlace();
            setForceUpdate?.(Date.now());
            return;
        }

        // 일반적인 경우 (취소 등)
        setActiveIdx(MapToolType.None);

        if (idx !== undefined && idx !== null && !isNaN(idx)) {
            changeContent(ContentList.List);
            setSelectedPoiIdx(idx);
        } else {
            changeContent(ContentList.Back);
        }
        deleteNewPlace();
    };

    /**
     * 신규 장소 일괄 등록 - 파일 삭제 시 관련 데이터 삭제 함수
     * @param fileIdx? 삭제한 파일의 인덱스
     * @param reset? 삭제 후 선택된 장소 인덱스 리셋 여부
     */
    const deleteNewPlace = (fileIdx?: number, reset?: boolean) => {
        // if (fileIdx) {
        if (fileIdx !== undefined) {
            const _newPlaceCoords = [...newPlaceCoords];
            _newPlaceCoords.splice(fileIdx, 1);

            setNewPlaceCoords(_newPlaceCoords);
        } else {
            setNewPlaceCoords([]);
        }

        setSelectedNewPlace(undefined);
        reset && setSelectedPoiIdx(0);
    };

    /**
     * 신규 장소 일괄 등록 - 지오코딩 성공 항목의 좌표 데이터 저장 함수
     * @author mjkang
     * @param coords 신규 장소 좌표 데이터
     */
    const addNewPlaceCoords = (coords: PlaceCoordsList) => {
        setNewPlaceCoords([...newPlaceCoords, coords]);
    };
    /**
     * Direction 함수
     */
    const finishDirectionAnalysis = (xidList: number[], polygon: Polygon) => {
        // 1. xid list로 contents 호출 _ 되도록 State set
        setAnalysisXIDs(xidList);
        // console.log("finishDirectionAnalysis - xidList: ", xidList);

        // 2. 분석 완료 시 즉시 List로 전환 (Accessibility와 동일한 동작)
        changeContent(ContentList.List);

        // 3. state에 Polygon 저장해서 map으로 넘겨준다(이후 map에서 지도에 폴리곤 그려줌)
        setIsochronePolygon(polygon);
    };

    /**
     * 맵 우측 상단 FloatingButton 렌더링 함수
     *
     * @param options {@link MapToolOptions}
     * @returns JSX[]
     */

    // 로그인 상태(쿠키) 체크
    // const isLogin = () => {
    //     const cookies = document.cookie.split("; ").reduce<Record<string, string>>((acc, cookie) => {
    //         const [key, value] = cookie.split("=");
    //         acc[key] = value;
    //         return acc;
    //     }, {});

    //     return cookies["KMLN"] == "1" && cookies["KMUI"] != undefined;
    // };
    // const isLogin = useLoginStatus(); // 로그인 방식 변경 | smyun | 250908 - 위로 이동

    type CallbackFn = () => void;
    const openLoginModal = (runToolAction: CallbackFn) => {
        openModal({
            title: "안내",
            children: (
                <>
                    로그인이 필요한 서비스입니다.
                    <br />
                    로그인을 하시겠습니까?
                </>
            ),
            modalButtons: (
                <ModalButtons
                    buttonCount={1}
                    buttons={[
                        {
                            label: "확인",
                            color: "Primary",
                            onClick: () =>
                                openModal({
                                    title: "로그인",
                                    children: (
                                        <LoginForm
                                            onClose={() => {
                                                closeModal();
                                                if (isLogin) {
                                                    runToolAction();
                                                }
                                            }}
                                            onClickSignUp={onClickSignUp}
                                        />
                                    ),
                                    modalButtons: null,
                                    size: "wide",
                                    disableOverlayClose: true,
                                }),
                        },
                    ]}
                />
            ),
        });
    };

    const onClickSignUp = () => {
        openModal({
            title: "회원가입",
            children: <SignUpModal onClose={() => closeModal()} />,
            modalButtons: null,
            size: "normal",
        });
    };

    const renderItem = (options: MapToolOptions) => {
        // 25.07.11 | Kim Young Jun | 모바일 뷰(높이 800미만) 여부
        const isLowHeight = window.innerHeight < 800;

        const hideInLowHeight = [
            MapToolType.Direction, // 시간별 이동거리
            MapToolType.Accessibility, // 접근성 분석
            MapToolType.ExtentAnalysis, // 범위 분석
            MapToolType.Overlap, // 오버랩 - 임시 비활성화
            MapToolType.Slope, // 경사도
        ];

        const permanentlyHidden = [
            MapToolType.Overlap, // 오버랩 기능 임시 비활성화
        ];

        const buttons = Object.keys(options).map((idx) => {
            const thisIdx = Number(idx);
            const option = options[thisIdx];

            // 25.01.13 | Kim Young Jun | 경사도 버튼의 활성화 상태는 isSlopeActive도 고려
            // 25.09.08 | Kim Young Jun | 오버랩 버튼의 활성화 상태도 URL 기반으로 확인
            const isActive =
                thisIdx === MapToolType.Slope
                    ? (activeIdx === thisIdx && slopeHook.isSlopeActive) || (isOverlapActive() && slopeHook.isSlopeActive)
                    : thisIdx === MapToolType.Overlap
                    ? isOverlapActive() || activeIdx === thisIdx
                    : activeIdx === thisIdx;

            const onClick = option.onClick;

            // 특정 도구는 mapInfo가 있어야 활성화
            if ([MapToolType.Region, MapToolType.Choropleth, MapToolType.Chart].includes(thisIdx)) {
                const baseMapInfo = getBaseMapInfo();

                const { cacheDate, updateDate } = baseMapInfo ?? {};
                const isNewMap = !cacheDate;
                const isUpdatedMap = !!(cacheDate && updateDate && new Date(updateDate) > new Date(cacheDate));
                option["disabled"] = isNewMap || isUpdatedMap;
            }

            // 25.09.02 | 오버랩 기능이 활성화된 상태에서 경사도를 제외한 다른 분석 도구 disable
            if (isOverlapActive() && thisIdx !== MapToolType.Slope && thisIdx !== MapToolType.Overlap) {
                option["disabled"] = true;
            }

            const handleClick = (e: MouseEvent<HTMLElement>) => {
                e.stopPropagation();

                // 25.09.02 | Kim Young Jun | 오버랩 버튼 클릭 처리 개선
                if (thisIdx === MapToolType.Overlap) {
                    setSelectedPoiIdx(0);
                    clearSelectedMarkerAndCloseSheet();

                    if (!isLogin) {
                        openLoginModal(() => window.location.reload());
                        return;
                    }
                    if (isOverlapModeURL()) {
                        // 오버랩 모드에서 오버랩 버튼 클릭 시 해제
                        // 25.09.08 | Kim Young Jun | 오버랩만 해제하고 경사도는 유지
                        resetOverlap();
                        return;
                    }
                    // 오버랩 모드가 아닌 경우
                    else {
                        // 이미 오버랩 버튼이 활성화되어 있으면 해제
                        if (activeIdxRef.current === MapToolType.Overlap) {
                            setActiveIdx(MapToolType.None);
                        } else {
                            setActiveIdx(MapToolType.Overlap);
                        }
                        return;
                    }
                }

                // 25.09.08 | Kim Young Jun | 경사도 분석 클릭 처리
                if (thisIdx === MapToolType.Slope) {
                    if (isOverlapActive()) {
                        // 오버랩 모드에서 경사도 클릭
                        if (isActive) {
                            // 경사도가 이미 활성화된 상태면 해제 (오버랩은 유지)
                            slopeHook.resetSlopeAnalysis();
                        } else {
                            // 경사도 활성화 (오버랩과 함께 사용)
                            slopeHook.startSlopeAnalysis();
                        }
                        return;
                    } else {
                        // 일반 모드에서 경사도 클릭
                        if (isActive) {
                            // 이미 활성화된 상태면 해제
                            setActiveIdx(MapToolType.None);
                            slopeHook.resetSlopeAnalysis();
                        } else {
                            // 경사도 활성화
                            setActiveIdx(thisIdx);
                        }
                        return;
                    }
                }

                const runToolAction = () => {
                    if (onClick) onClick(e, !isActive);

                    // 모바일 뷰에서 Selector가 있는 분석도구인 경우
                    const hasSelectorTools = [MapToolType.Choropleth, MapToolType.Heat, MapToolType.ExtentAnalysis];

                    if (isMobileView && hasSelectorTools.includes(thisIdx)) {
                        if (isActive) {
                            // 활성화된 상태에서 클릭 시
                            if (showAnalysisSelector[thisIdx]) {
                                // Selector가 열려있으면 분석 해제
                                setCurrentRegionCode && setCurrentRegionCode("0");
                                setIsChoroplethAnalysis(false);
                                setActiveIdx(MapToolType.None);
                                setShowAnalysisSelector((prev) => ({
                                    ...prev,
                                    [thisIdx]: false,
                                }));
                            } else {
                                // Selector가 닫혀있으면 Selector 열기
                                setShowAnalysisSelector((prev) => ({
                                    ...prev,
                                    [thisIdx]: true,
                                }));
                                startSelectorCloseTimer(thisIdx);
                            }
                        } else {
                            // 비활성화 상태에서 클릭 시 (최초 클릭)
                            setActiveIdx(thisIdx);
                            // console.log("setActiveIdx thisIdx: ", thisIdx);

                            if (thisIdx === MapToolType.Choropleth) {
                                setIsChoroplethAnalysis(true);
                                setLoadingMessage(`단계구분도 '장소 개수' 항목을 분석중입니다.`);
                                setIsLoading(true);
                                triggerAnalysis();
                            } else if (thisIdx === MapToolType.Heat) {
                                setLoadingMessage(`히트맵 '장소 개수' 항목을 분석중입니다.`);
                                setIsLoading(true);
                                triggerAnalysis();
                            }

                            setShowAnalysisSelector((prev) => ({
                                ...prev,
                                [thisIdx]: true,
                            }));
                            startSelectorCloseTimer(thisIdx);
                        }
                    } else {
                        // PC 뷰 또는 Selector가 없는 도구들의 기존 로직
                        if (isActive) {
                            setCurrentRegionCode && setCurrentRegionCode("0");
                            setIsChoroplethAnalysis(false);
                            setActiveIdx(MapToolType.None);
                            // 활성화 해제 시 모든 Selector 숨기기
                            setShowAnalysisSelector((prev) => ({
                                ...prev,
                                [thisIdx]: false,
                            }));
                        } else {
                            if (thisIdx === MapToolType.Choropleth) {
                                setIsChoroplethAnalysis(true);
                                // 단계구분도 활성화 시 기본 분석 시작 및 Selector 표시
                                setLoadingMessage(`단계구분도 '장소 개수' 항목을 분석중입니다.`);
                                setIsLoading(true);
                                triggerAnalysis();
                                setShowAnalysisSelector((prev) => ({
                                    ...prev,
                                    [thisIdx]: true,
                                }));
                                // 1.5초 후 자동 닫기 타이머 시작
                                startSelectorCloseTimer(thisIdx);
                            } else if (thisIdx === MapToolType.Heat) {
                                // 히트맵 활성화 시 기본 분석 시작 및 Selector 표시
                                setLoadingMessage(`히트맵 '장소 개수' 항목을 분석중입니다.`);
                                setIsLoading(true);
                                triggerAnalysis();
                                setShowAnalysisSelector((prev) => ({
                                    ...prev,
                                    [thisIdx]: true,
                                }));
                                // 1.5초 후 자동 닫기 타이머 시작
                                startSelectorCloseTimer(thisIdx);
                                // } else if (thisIdx === MapToolType.Slope) {
                                //     setLoadingMessage(`경사도 분석을 시작합니다.`);
                                //     setIsLoading(true);
                                //     triggerAnalysis();
                                //     setShowAnalysisSelector((prev) => ({
                                //         ...prev,
                                //         [thisIdx]: true,
                                //     }));
                                //     // 1.5초 후 자동 닫기 타이머 시작
                                //     startSelectorCloseTimer(thisIdx);
                            } else if (thisIdx === MapToolType.ExtentAnalysis) {
                                // 범위분석 활성화 시 Selector 표시
                                setShowAnalysisSelector((prev) => ({
                                    ...prev,
                                    [thisIdx]: true,
                                }));
                                // 1.5초 후 자동 닫기 타이머 시작
                                startSelectorCloseTimer(thisIdx);
                            }

                            setActiveIdx(thisIdx);
                        }
                    }
                };

                // 장소추가 기능일 경우
                if (option.label === "장소추가") {
                    if (isLogin) {
                        runToolAction();
                    } else {
                        openLoginModal(runToolAction); // 로그인 모달
                    }
                } else {
                    runToolAction();
                    // 25.07.07 | Kim Young Jun | 선택된 피처 하이라이트 해제 (오버랩 도구는 제외)
                    if (thisIdx !== MapToolType.Overlap) {
                        setSelectedPoiIdx(0);
                    }
                }
            };

            // 25.07.18 | Kim Young Jun | 분석 버튼 호버 시 Selector 표시 (PC)
            const handleMouseEnter = () => {
                if (isActive && (thisIdx === MapToolType.Choropleth || thisIdx === MapToolType.Heat || thisIdx === MapToolType.ExtentAnalysis)) {
                    cancelMenuCloseTimer();
                    setShowAnalysisSelector((prev) => ({
                        ...prev,
                        [thisIdx]: true,
                    }));
                }
            };

            const handleMouseLeave = () => {
                if (isActive && (thisIdx === MapToolType.Choropleth || thisIdx === MapToolType.Heat || thisIdx === MapToolType.ExtentAnalysis)) {
                    startSelectorCloseTimer(thisIdx);
                }
            };

            // 상태 반영
            option["isActive"] = isActive;
            option["onClick"] = handleClick;

            // 조건별 Selector 컴포넌트 렌더링
            const renderExtraComponent = () => {
                if (!isAnalysisMenuOpen || !isActive) return null;

                switch (thisIdx) {
                    case MapToolType.ExtentAnalysis:
                        if (option.label === "범위분석" && showAnalysisSelector[thisIdx]) return renderExtentAnalysisTool(thisIdx);
                        break;
                    case MapToolType.Choropleth:
                        if (option.label === "단계 구분도" && showAnalysisSelector[thisIdx]) return renderChoroplethAnalysisTool(thisIdx);
                        break;
                    case MapToolType.Heat:
                        if (option.label === "히트맵" && showAnalysisSelector[thisIdx]) return renderHeatmapAnalysisTool(thisIdx);
                        break;
                    default:
                        return null;
                }
            };

            const extraSelector = renderExtraComponent();

            // 버튼 렌더링
            // 25.07.11 | Kim Young Jun | 모바일 뷰일 때 버튼 크기 수정
            return extraSelector ? (
                <div
                    className={styles.extentAnalysisMenu}
                    key={`tools_${thisIdx}`}
                    onMouseEnter={!isMobileView ? handleMouseEnter : undefined}
                    onMouseLeave={!isMobileView ? handleMouseLeave : undefined}
                >
                    <FloatingButton {...option} width={isMobileView ? "36px" : "42px"} />
                    {extraSelector}
                </div>
            ) : (
                <div
                    key={`tools_${thisIdx}`}
                    onMouseEnter={!isMobileView ? handleMouseEnter : undefined}
                    onMouseLeave={!isMobileView ? handleMouseLeave : undefined}
                >
                    <FloatingButton {...option} width={isMobileView ? "36px" : "42px"} />
                </div>
            );
        });

        return buttons;
    };

    // 확장 툴 셀렉터 인덱스 State

    const [analysisMetricsToolIdx, setAnalysisMetricsToolIdx] = useState(21);
    const [extentToolIdx, setExtentToolIdx] = useState(25);

    const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

    // 범위분석 툴 렌더
    // const renderExtentAnalysisTool = () => {
    //     const tools = Object.keys(extentToolOptions).map((idx) => {
    //         const thisIdx = Number(idx);
    //         const option = extentToolOptions[thisIdx];
    //         const isActive = extentToolIdx === thisIdx;
    //         const isHovering = hoveredIdx === thisIdx;

    //         const icon = option.icon;
    //         const label = option.label;
    //         const onClick = option.onClick;

    //         const handleClick = (e: MouseEvent<HTMLElement>) => {
    //             setExtentToolIdx(thisIdx);
    //             if (onClick) onClick(e, !isActive);
    //         };

    //         option["isActive"] = isActive;
    //         option["onClick"] = handleClick;

    //         return (
    //             <div
    //                 className={`${styles.extentTool} ${isActive ? styles.active : ""}`}
    //                 key={`extentTools_${thisIdx}`}
    //                 onClick={handleClick}
    //                 onMouseOver={() => handleMouseOver(thisIdx)}
    //                 onMouseOut={handleMouseOut}
    //             >
    //                 {icon && <Icon name={icon} size={"small"} color={isActive ? "white" : isHovering ? "default" : "darkGrey"} />}
    //                 {label && <span className={`${!isActive && isHovering ? styles.hovering : ""}`}>{label}</span>}
    //             </div>
    //         );
    //     });

    //     return <div className={styles.extentToolContainer}>{tools}</div>;
    // };

    // DropdownSelectorItems 추출
    const extractDropdownSelectorItems = (allPlaceData: Record<number, PlaceInfoSet>): DropdownSelectorItems[] => {
        const toolOptionsLastIdx = Math.max(...(Object.values(MapToolType).filter((v) => typeof v === "number") as number[]));

        if (!allPlaceData || Object.keys(allPlaceData).length === 0) return [];

        // 첫 번째 항목의 placeInfo를 기준으로 키 추출
        const samplePlace = Object.values(allPlaceData)[0];

        if (!samplePlace || !samplePlace.placeInfo) return [];

        const dropdownItems: DropdownSelectorItems[] = [];

        const placeInfoKeys = Object.keys(samplePlace.placeInfo).filter((key) => key !== "XID");

        placeInfoKeys.forEach((key, idx) => {
            // 제외할 키워드 목록
            const excludedKeywords = ["date", "일자", "날짜", "code", "코드", "인덱스", "index", "번호"];
            const isExcluded = excludedKeywords.some((kw) => key.toLowerCase().includes(kw.toLowerCase()));
            if (isExcluded) return;

            const allValuesAreNumberLike = Object.values(allPlaceData).every((placeSet) => {
                const val = placeSet.placeInfo[key];

                if (val === null) return true;

                if (typeof val === "string") {
                    const cleaned = val.replace(/,/g, "");
                    return !isNaN(Number(cleaned));
                }

                return typeof val === "number";
            });

            if (allValuesAreNumberLike) {
                const toolKey = toolOptionsLastIdx + 1 + idx;

                dropdownItems.push({
                    key: String(toolKey),
                    label: key,
                    isActive: analysisMetricsToolIdx == toolKey,
                    isHover: hoveredIdx === toolKey,
                });
            }
        });

        return dropdownItems;
    };

    const enrichedItems: DropdownSelectorItems[] = dropdownSelectorItems.map((item) => {
        const keyNum = Number(item.key);
        return {
            ...item,
            isActive: keyNum === analysisMetricsToolIdx,
            isHover: keyNum === hoveredIdx,
        };
    });

    // 25.07.18 | Kim Young Jun | 단계구분도 툴 렌더 (toolType 매개변수 추가)
    const renderChoroplethAnalysisTool = (toolType: MapToolType) => {
        const selectorItems: SelectorItems[] = Object.keys(analysisMetricOptions).map((idx) => {
            const thisIdx = Number(idx);
            const option = analysisMetricOptions[thisIdx];

            const isActive = analysisMetricsToolIdx === thisIdx;
            const isHover = hoveredIdx === thisIdx;

            option["isActive"] = isActive;

            return {
                key: String(thisIdx),
                icon: option.icon ?? "",
                label: option.label ?? "",
                isActive: isActive,
                isHover: isHover,
            };
        });

        const handleSelect = (key: string, label?: string) => {
            const idx = Number(key);

            setAnalysisMetricsToolIdx(idx);

            if (label) {
                // console.log("[Select " + label + "]");
                setSelectedDropdownField(label);
            }

            const option = analysisMetricOptions[idx];

            setLoadingMessage(`단계구분도 '${option.label ?? ""}' 항목을 분석중입니다.`);
            setIsLoading(true);
            triggerAnalysis();

            if (option?.onClick) {
                option.onClick(true);
            }
        };

        return (
            <div
                className={styles.extentToolContainer}
                onMouseEnter={!isMobileView ? cancelMenuCloseTimer : undefined}
                onMouseLeave={!isMobileView ? () => startSelectorCloseTimer(toolType) : undefined}
            >
                <AnalysisMetricSelector
                    analysisType={MapToolType.Choropleth}
                    selectorItems={selectorItems}
                    dropdownSelectorItems={enrichedItems}
                    selectedKey={String(analysisMetricsToolIdx)}
                    onChange={handleSelect}
                    isReverse={true}
                    onMouseOver={(key) => {
                        setHoveredIdx(Number(key));
                        cancelMenuCloseTimer();
                    }}
                    onMouseOut={() => {
                        setHoveredIdx(null);
                        startSelectorCloseTimer(toolType);
                    }}
                    setIsLoading={setIsLoading}
                    setLoadingMessage={setLoadingMessage}
                    triggerAnalysis={triggerAnalysis}
                />
            </div>
        );
    };

    // 히트맵 툴 렌더
    const renderHeatmapAnalysisTool = (toolType: MapToolType) => {
        const selectorItems: SelectorItems[] = Object.keys(analysisMetricOptions).map((idx) => {
            const thisIdx = Number(idx);
            const option = analysisMetricOptions[thisIdx];

            const isActive = analysisMetricsToolIdx === thisIdx;
            const isHover = hoveredIdx === thisIdx;

            option["isActive"] = isActive;

            return {
                key: String(thisIdx),
                icon: option.icon ?? "",
                label: option.label ?? "",
                isActive: isActive,
                isHover: isHover,
            };
        });

        // extractDropdownSelectorItems(allPlaceData);

        const handleSelect = (key: string, label?: string) => {
            const idx = Number(key);

            setAnalysisMetricsToolIdx(idx);

            if (label) {
                // console.log("[Select " + label + "]");
                setSelectedDropdownField(label);
            }

            const option = analysisMetricOptions[idx];

            // 25.07.07 | Kim Young Jun | setIsLoading, setLoadingMessage 추가 및 트리거 활성화
            setLoadingMessage(`히트맵 '${option.label ?? ""}' 항목을 분석중입니다.`);
            setIsLoading(true);
            triggerAnalysis();

            if (option?.onClick) {
                option.onClick(true);
            }
        };

        return (
            <div
                className={styles.extentToolContainer}
                onMouseEnter={!isMobileView ? cancelMenuCloseTimer : undefined}
                onMouseLeave={!isMobileView ? () => startSelectorCloseTimer(toolType) : undefined}
            >
                <AnalysisMetricSelector
                    analysisType={MapToolType.Heat}
                    selectorItems={selectorItems}
                    dropdownSelectorItems={enrichedItems}
                    selectedKey={String(analysisMetricsToolIdx)}
                    onChange={handleSelect}
                    isReverse={true}
                    onMouseOver={(key) => setHoveredIdx(Number(key))}
                    onMouseOut={() => setHoveredIdx(null)}
                    // 25.07.03 | Kim Young Jun | setIsLoading, setLoadingMessage 추가
                    setIsLoading={setIsLoading}
                    setLoadingMessage={setLoadingMessage}
                    triggerAnalysis={triggerAnalysis}
                />
            </div>
        );
    };

    // 25.07.18 | Kim Young Jun | 범위분석 툴 렌더 (toolType 매개변수 추가)
    const renderExtentAnalysisTool = (toolType: MapToolType) => {
        const selectorItems: SelectorItems[] = Object.keys(extentToolOptions).map((idx) => {
            const thisIdx = Number(idx);
            const option = extentToolOptions[thisIdx];

            const isActive = extentToolIdx === thisIdx;
            const isHover = hoveredIdx === thisIdx;

            option["isActive"] = isActive;

            return {
                key: String(thisIdx),
                icon: option.icon ?? "",
                label: option.label ?? "",
                isActive: isActive,
                isHover: isHover,
            };
        });

        const handleSelect = (key: string) => {
            const idx = Number(key);
            setExtentToolIdx(idx);

            const option = extentToolOptions[idx];

            if (option?.onClick) {
                option.onClick(true);
            }
        };

        return (
            <div
                className={styles.extentToolContainer}
                onMouseEnter={!isMobileView ? cancelMenuCloseTimer : undefined}
                onMouseLeave={!isMobileView ? () => startSelectorCloseTimer(toolType) : undefined}
            >
                <Selector
                    items={selectorItems}
                    selectedKey={String(extentToolIdx)}
                    onChange={handleSelect}
                    isReverse={true}
                    onMouseOver={
                        !isMobileView
                            ? (key) => {
                                  setHoveredIdx(Number(key));
                                  cancelMenuCloseTimer();
                              }
                            : undefined
                    }
                    onMouseOut={
                        !isMobileView
                            ? () => {
                                  setHoveredIdx(null);
                                  startSelectorCloseTimer(toolType);
                              }
                            : undefined
                    }
                />
            </div>
        );
    };

    // 사이드 시트 컨텐츠 영역 -----------------------------------------------
    const renderContent = (type: ContentList) => {
        switch (type) {
            case ContentList.List:
                return (
                    <POIListView
                        ref={sidesheetPoiListRef}
                        POIList={POIList}
                        categories={getCurrentCategories()}
                        // 25.09.02 | Kim Young Jun | selectedCategoryArray 기반으로 변경
                        selectedCategoryArray={selectedCategoryArray}
                        selectedMapIdx={selectedMapIdx}
                        setSelectedCategory={setCurrentSelectedCategory}
                        selectedPoiIdx={selectedPoiIdx}
                        setSelectedPoiIdx={setSelectedPoiIdx}
                        analysisXIDs={analysisXIDs}
                        setAnalysisXIDs={setAnalysisXIDs}
                        overlapMapId={
                            // 25.09.02 | Kim Young Jun | selectedMapIdx 기반 올바른 baseMapID 매핑 (디버깅 로그 추가)
                            (() => {
                                let selectedMapId;
                                if (selectedMapIdx === 0) {
                                    selectedMapId = baseMapID || undefined; // 기본 지도 (인덱스 0)
                                } else if (selectedMapIdx > 0 && getOverlapMapInfos().length >= selectedMapIdx) {
                                    const overlapMaps = getOverlapMapInfos();
                                    selectedMapId = overlapMaps[selectedMapIdx - 1].baseMapID; // 오버랩 지도 (인덱스 1부터 시작)
                                } else {
                                    selectedMapId = baseMapID || undefined; // 폴백
                                }
                                const overlapMapsLength = getOverlapMapInfos().length;
                                return selectedMapId;
                            })()
                        }
                        changeContent={changeContent}
                        activeTool={activeIdx}
                        setClearByButton={setClearByButton}
                        analysisStage={analysisStage}
                        // resetPOIListView={resetPOIListView}
                        setResetAcc={setResetAcc}
                        prevSelectedCategoryRef={prevSelectedCategoryRef}
                        searchOptions={searchOptions}
                        setSearchOptions={setSearchOptions}
                        listSearchOptions={listSearchOptions}
                        setListSearchOptions={setListSearchOptions}
                        isNotUseMarkerAnalysis={isNotUseMarkerAnalysis()} // 25.07.17 | Kim Young Jun | 마커를 사용하지 않는 분석도구 활성화 여부
                    />
                );
            case ContentList.AccountUpgrade:
                return <AccountUpgrade changeContent={changeContent} />;
            case ContentList.AddPlace:
                return <AddPlace changeContent={changeContent} />;
            case ContentList.AddSinglePlace:
                return (
                    <SinglePlaceFormArea
                        coords={newPlaceCoords}
                        categories={getCurrentCategories()}
                        closeForm={closeForm}
                        setNewPlace={setSelectedNewPlace}
                        setCoords={setNewPlaceCoords}
                        toggleAddMode={setIsAddMode}
                        setToast={setToast}
                        setEditPlaceStatus={setEditPlaceStatus}
                        newPlaceAddress={newPlaceAddress}
                        setNewPlaceAddress={setNewPlaceAddress}
                        agreeNoneAddress={agreeNoneAddress}
                        setAgreeNoneAddress={setAgreeNoneAddress}
                        revGeoLoading={revGeoLoading}
                        setActiveIdx={setActiveIdx}
                        customModal={{ openModal: openModal, closeModal: closeModal }}
                    />
                );
            case ContentList.AddMultiplePlaces:
                return (
                    <AddMultiplePlaces
                        changeContent={changeContent}
                        progress={progress}
                        coords={newPlaceCoords}
                        addResult={addNewPlaceCoords}
                        deleteResult={deleteNewPlace}
                        selectedPoiIdx={typeof selectedPoiIdx === "string" ? selectedPoiIdx : undefined}
                        setSelectedPlace={setSelectedNewPlace}
                        categories={getCurrentCategories()}
                    />
                );
            case ContentList.EditPlace:
                return (
                    <SinglePlaceFormArea
                        coords={newPlaceCoords}
                        categories={getCurrentCategories()}
                        XID={typeof selectedPoiIdx === "number" ? selectedPoiIdx : undefined}
                        closeForm={closeForm}
                        setCoords={setNewPlaceCoords}
                        toggleAddMode={setIsAddMode}
                        setToast={setToast}
                        setEditPlaceStatus={setEditPlaceStatus}
                        newPlaceAddress={newPlaceAddress}
                        setNewPlaceAddress={setNewPlaceAddress}
                        revGeoLoading={revGeoLoading}
                        setActiveIdx={setActiveIdx}
                        agreeNoneAddress={agreeNoneAddress}
                        setAgreeNoneAddress={setAgreeNoneAddress}
                    />
                );

            // 시간별 이동거리
            case ContentList.Direction:
                return (
                    <DirectionAnalysis
                        changeContent={changeContent}
                        startPoint={directionStartPoint}
                        setStartPoint={setDirectionStartPoint}
                        mapProj={mapProj}
                        mapId={baseMapID}
                        finishAnalysis={finishDirectionAnalysis}
                        setAnalysisStage={setAnalysisStage}
                        setClearByButton={setClearByButton}
                        setDirectionVehicle={setDirectionVehicle}
                        setIsLoading={setIsLoading} // 25.07.03 | Kim Young Jun | 로딩 상태 설정
                        openMapScreenModal={openModal} // 25.07.14 | Kim Young Jun | 모달 열기 함수
                        closeMapScreenModal={closeModal} // 25.07.14 | Kim Young Jun | 모달 닫기 함수
                    />
                );
            // 접근성분석
            case ContentList.Accessibility:
                return <AccessibilityAnalysis changeContent={changeContent} accState={accState} setAccState={setAccState} />;

            // 오버랩
            case ContentList.Overlap:
                return <OverlapAnalysis changeContent={changeContent} />;
            case ContentList.Loading:
                return <LoadingSpinner />;
            default:
                return (
                    <POIListView
                        POIList={POIList}
                        categories={getCurrentCategories()}
                        // 25.09.02 | Kim Young Jun | selectedCategoryArray 기반으로 변경
                        selectedCategoryArray={selectedCategoryArray}
                        selectedMapIdx={selectedMapIdx}
                        setSelectedCategory={setCurrentSelectedCategory}
                        selectedPoiIdx={selectedPoiIdx}
                        setSelectedPoiIdx={setSelectedPoiIdx}
                        analysisXIDs={analysisXIDs}
                        setAnalysisXIDs={setAnalysisXIDs}
                        overlapMapId={
                            // 25.09.02 | Kim Young Jun | selectedMapIdx 기반 올바른 baseMapID 매핑 (디버깅 로그 추가)
                            (() => {
                                let selectedMapId;
                                if (selectedMapIdx === 0) {
                                    selectedMapId = baseMapID || undefined; // 기본 지도 (인덱스 0)
                                } else if (selectedMapIdx > 0 && getOverlapMapInfos().length >= selectedMapIdx) {
                                    const overlapMaps = getOverlapMapInfos();
                                    selectedMapId = overlapMaps[selectedMapIdx - 1].baseMapID; // 오버랩 지도 (인덱스 1부터 시작)
                                } else {
                                    selectedMapId = baseMapID || undefined; // 폴백
                                }
                                const overlapMapsLength = getOverlapMapInfos().length;
                                // console.log(
                                //     `[POI_MAP_ID_DEFAULT] selectedMapIdx=${selectedMapIdx}, selectedMapId=${selectedMapId}, baseMapId=${baseMapID}, overlapMapInfos.length=${overlapMapsLength}`
                                // );
                                return selectedMapId;
                            })()
                        }
                        changeContent={changeContent}
                        activeTool={activeIdx}
                        setClearByButton={setClearByButton}
                        analysisStage={analysisStage}
                        // resetPOIListView={resetPOIListView}
                        setResetAcc={setResetAcc}
                        searchOptions={searchOptions}
                        setSearchOptions={setSearchOptions}
                        listSearchOptions={listSearchOptions}
                        setListSearchOptions={setListSearchOptions}
                    />
                );
        }
    };

    const renderProgressBar = () => {
        return createPortal(
            <div className={styles.hide_map} style={{ paddingLeft: sideSheetWidth }}>
                <div className={styles.progress}>
                    <ProgressBar state={geocodingState} label={["파일을 읽고 있습니다.", "조금만 기다려주세요."]} />
                </div>
            </div>,
            document.body
        );
    };

    // 히트맵 blur 조절 바 ---------------------------------------------------
    const renderBlurBar = () => {
        const maxBlur = 50;
        const uiValue = maxBlur - blur; // 0이면 진하고 50이면 흐림

        return (
            <form>
                <label htmlFor="blur">투명도</label>
                <input
                    id="blur"
                    type="range"
                    min="0"
                    max={maxBlur}
                    step="10"
                    value={uiValue}
                    onChange={(e) => {
                        const raw = parseInt(e.target.value, 10);
                        const computed = maxBlur - raw;
                        setBlur(computed);
                    }}
                    className={isMobileView ? styles.mobileSlider : styles.pcSlider}
                />
            </form>
        );
    };

    const renderHLegend = () => {
        return (
            <>
                <span>C</span>
                <div className={styles.bar}></div>
                <span>H</span>
            </>
        );
    };

    // 25.07.10 | Kim Young Jun | 범례 렌더링 로직 수정 (소수점 처리, 범례 값이 없는 경우, 등간격 수정 등)
    const renderLegend = () => {
        let isLegendNotZero = false;

        if (!legend || legend.length === 0 || legend.every((item) => item.max === 0 && item.min === 0)) {
            isLegendNotZero = true; // 범례가 없거나 모든 항목이 0인 경우
        }

        // 소수점 끝자리에서 1을 빼는 함수
        const subtractFromLastDecimal = (num: number) => {
            // 소수점이 없는 경우 (정수)
            if (Number.isInteger(num)) {
                return num - 1;
            }

            // 소수점이 있는 경우
            const strNum = num.toString();
            const decimalPlaces = strNum.includes(".") ? strNum.split(".")[1].length : 0;

            // 소수점 끝자리에서 1을 빼기 위해 10^소수점 자릿수로 나눈 값을 뺌
            return num - 1 / Math.pow(10, decimalPlaces);
        };

        // max 값의 소수점 자릿수에 따라 최소값과 포맷팅 설정 결정하는 함수
        const getMinValueAndFormat = (maxValue: number): [number, number] => {
            if (Number.isInteger(maxValue)) {
                return [1, 0]; // 최대값이 정수면 최소값은 1, 소수점 자리는 0
            } else {
                const strNum = maxValue.toString();
                const decimalPlaces = strNum.includes(".") ? strNum.split(".")[1].length : 0;

                // console.log("[DBG] getMinValueAndFormat: maxValue = " + maxValue + ", decimalPlaces = " + decimalPlaces);
                // 소수점 자릿수만큼 0.1, 0.01, 0.001 등으로 설정
                return [1 / Math.pow(10, decimalPlaces), decimalPlaces];
            }
        };

        return (
            legend && (
                <div id={styles.legend}>
                    {/* <div className={styles.title}>{"범례"}</div> */}
                    <div className={styles.desc} key={0}>
                        <span
                            className={styles.color}
                            style={{
                                backgroundColor: "white",
                                border: "0.5px solid pink",
                                boxSizing: "border-box",
                            }}
                        >
                            {" "}
                        </span>
                        <span>{formatNumber(0)} 이하</span>
                    </div>
                    {!isLegendNotZero &&
                        legend.map((item, idx) => {
                            // 마지막 항목인지 확인
                            const isLastItem = idx === legend.length - 1;
                            // 마지막 항목이 아닌 경우에만 소수점 끝자리에서 1을 뺌
                            const maxValue = isLastItem ? item.max : subtractFromLastDecimal(item.max);

                            // 첫 번째 항목의 경우 max값 기준으로 min 값과 소수점 자릿수 계산
                            let minValue = item.min;
                            let decimalPlaces = 0;

                            if (idx === 0) {
                                // max 값의 소수점 자릿수에 따라 minValue와 decimalPlaces 결정
                                [minValue, decimalPlaces] = getMinValueAndFormat(item.max);
                            }

                            return (
                                <div className={styles.desc} key={idx + 1}>
                                    <span
                                        className={styles.color}
                                        style={{
                                            backgroundColor: getColorByClass(item.class, legend.length, 0),
                                        }}
                                    >
                                        {" "}
                                    </span>
                                    <span>
                                        {idx === 0
                                            ? // ? `${minValue.toFixed(decimalPlaces)} 이상 ~ ${formatNumber(maxValue)} 이하`
                                              `${formatNumber(minValue.toFixed(decimalPlaces))} 이상 ~ ${formatNumber(maxValue)} 이하`
                                            : item.min < item.max
                                            ? `${formatNumber(item.min)} 이상 ~ ${formatNumber(maxValue)} 이하`
                                            : `${formatNumber(item.min)} 이상`}
                                    </span>
                                </div>
                            );
                        })}
                </div>
            )
        );
    };

    const renderCircleLegend = () => {
        return (
            legend &&
            (() => {
                const MAX_LEGEND_COUNT = 4;

                // legend 배열에서 등간격으로 4개 추출 또는 min/max 보정
                const sampledLegend = (() => {
                    if (legend.length <= MAX_LEGEND_COUNT) {
                        // 모든 항목의 min = max로 보정
                        const adjustedLegend = legend.map((item) => ({
                            ...item,
                            max: item.min, // 또는 item.min, 기준에 따라
                        }));

                        // min 값 기준 중복 제거 (겹치는 라벨 방지)
                        const uniqueLegend = adjustedLegend.filter((item, index, self) => index === self.findIndex((t) => t.min === item.min));
                        return uniqueLegend;
                    }

                    // 등간격 추출
                    const result = [];
                    const step = (legend.length - 1) / (MAX_LEGEND_COUNT - 1);
                    for (let i = 0; i < MAX_LEGEND_COUNT; i++) {
                        const index = Math.round(i * step);
                        result.push(legend[index]);
                    }

                    return result;
                })();

                return (
                    <div id={styles.legend} className={styles.circle_legend}>
                        <div className={styles.circle_wrapper}>
                            <div className={styles.circle_label_container}>
                                <span className={styles.circle_label} style={{ bottom: 10 }}>
                                    0
                                    <span className={styles.tick} />
                                </span>
                                {sampledLegend
                                    .slice()
                                    .reverse()
                                    .map((item, idx) => {
                                        const size = getCircleRadius(item.class, legend.length, 0);
                                        return (
                                            <span
                                                className={styles.circle_label}
                                                key={"label_" + idx}
                                                style={{
                                                    bottom: size + 2,
                                                }}
                                            >
                                                {formatNumber(item.min)}
                                                <span className={styles.tick} />
                                            </span>
                                        );
                                    })}
                            </div>
                            <div className={styles.circle_container}>
                                {sampledLegend.map((item, idx) => {
                                    const size = getCircleRadius(item.class, legend.length, 0);
                                    return (
                                        <div
                                            key={"circle_" + idx}
                                            className={styles.circle}
                                            style={{
                                                width: size,
                                                height: size,
                                                backgroundColor: "rgba(0, 119, 255, 0.3)",
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                );
            })()
        );
    };

    const renderSlopeLegend = useMemo(() => {
        return (
            <div id={styles.legend} style={{ opacity: 0.7, width: "200px" }}>
                <div className={styles.slope_desc} key={0}>
                    <span className={styles.slope_color} style={{ backgroundColor: "white" }}>
                        {" "}
                    </span>
                    <div className={styles.slope_span}>
                        <span>경사 없음</span>
                        <span>0%</span>
                    </div>
                </div>
                <div className={styles.slope_desc} key={1}>
                    <span className={styles.slope_color} style={{ backgroundColor: "#FFCF06" }}>
                        {" "}
                    </span>
                    <div className={styles.slope_span}>
                        <span>얕은 경사 </span>
                        <span>2~4%</span>
                    </div>
                </div>
                <div className={styles.slope_desc} key={2}>
                    <span className={styles.slope_color} style={{ backgroundColor: "#C6DE00" }}>
                        {" "}
                    </span>
                    <div className={styles.slope_span}>
                        <span>오르막길</span>
                        <span>5~8%</span>
                    </div>
                </div>
                <div className={styles.slope_desc} key={3}>
                    <span className={styles.slope_color} style={{ backgroundColor: "#8CD92E" }}>
                        {" "}
                    </span>
                    <div className={styles.slope_span}>
                        <span>숨이 차는 경사</span>
                        <span>9~11%</span>
                    </div>
                </div>
                <div className={styles.slope_desc} key={4}>
                    <span className={styles.slope_color} style={{ backgroundColor: "#0A9E00" }}>
                        {" "}
                    </span>
                    <div className={styles.slope_span}>
                        <span>더 힘든 경사</span>
                        <span>12~15%</span>
                    </div>
                </div>
                <div className={styles.slope_desc} key={5}>
                    <span className={styles.slope_color} style={{ backgroundColor: "#5876AA" }}>
                        {" "}
                    </span>
                    <div className={styles.slope_span}>
                        <span>등산하는 느낌</span>
                        <span>16~%</span>
                    </div>
                </div>
            </div>
        );
    }, []);

    const escEvent = (e: KeyboardEvent) => {
        const currentIdx = activeIdxRef.current ?? -1;
        const measureTools = [MapToolType.Distance, MapToolType.Area, MapToolType.Radius];

        if (measureTools.includes(currentIdx)) {
            e.key.toLowerCase() === "escape" && setActiveIdx(MapToolType.None);
        }
    };

    const [isAnalysisMenuOpen, setIsAnalysisMenuOpen] = useState(false);

    // 25.07.18 | Kim Young Jun | 메뉴 자동 닫기를 위한 타이머 ref 추가
    const menuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // 25.07.18 | Kim Young Jun | 개별 분석도구 메뉴 표시 상태 추가
    const [showAnalysisSelector, setShowAnalysisSelector] = useState<{ [key: number]: boolean }>({
        [MapToolType.Choropleth]: false,
        [MapToolType.Heat]: false,
        [MapToolType.ExtentAnalysis]: false,
    });

    // 25.07.18 | Kim Young Jun | 개별 분석도구 메뉴 자동 닫기 함수
    const startSelectorCloseTimer = (toolType: MapToolType) => {
        // 기존 타이머가 있으면 취소
        if (menuCloseTimerRef.current) {
            clearTimeout(menuCloseTimerRef.current);
        }

        // 2.5초 후 해당 도구의 Selector만 닫기
        menuCloseTimerRef.current = setTimeout(() => {
            setShowAnalysisSelector((prev) => ({
                ...prev,
                [toolType]: false,
            }));
        }, 2500);
    };

    // 25.07.18 | Kim Young Jun | 메뉴 닫기 타이머 취소 함수
    const cancelMenuCloseTimer = () => {
        if (menuCloseTimerRef.current) {
            clearTimeout(menuCloseTimerRef.current);
            menuCloseTimerRef.current = null;
        }
    };

    // 25.07.18 | Kim Young Jun | 컴포넌트 언마운트 시 타이머 정리
    useEffect(() => {
        return () => {
            if (menuCloseTimerRef.current) {
                clearTimeout(menuCloseTimerRef.current);
            }
        };
    }, []);

    const toggleMapToolButtons = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();

        const elem = e.currentTarget as HTMLElement;
        const parent = elem.parentElement;
        if (!parent) return;
        const parentSibling = (parent.previousElementSibling ?? parent.nextElementSibling) as HTMLElement;
        if (!parentSibling) return;

        const classList = parent.classList;

        if (classList.contains(styles.expanded)) {
            // 접기
            setIsAnalysisMenuOpen(false);
            parent.style.maxHeight = "";
            classList.remove(styles.expanded);

            const arrow = elem.firstElementChild as HTMLElement;
            arrow.style.transform = "";
        } else {
            // 펼치기
            setTimeout(() => {
                setIsAnalysisMenuOpen(true);
            }, 150);
            const hight = parent.scrollHeight + 20;
            parent.style.maxHeight = `${hight}px`;
            classList.add(styles.expanded);

            const arrow = elem.firstElementChild as HTMLElement;
            arrow.style.transform = "rotateX(180deg)";

            parentSibling.style.maxHeight = "";
            parentSibling.classList.remove(styles.expanded);

            const sibling = parentSibling.firstElementChild;
            if (!sibling) return;

            const siblingArrow = sibling.firstElementChild as HTMLElement;
            siblingArrow.style.transform = "";
        }
    };

    const handlePOIReload = () => {
        sidesheetPoiListRef.current?.reload();
    };

    // 25.08.13 | Kim Young Jun | 북마크 상태 관리 함수 추가
    const handleBookmarkChange = useCallback(
        async (newBookmark: boolean) => {
            // console.log("=== MapScreen handleBookmarkChange 시작 ===");
            // console.log("새로운 북마크 상태:", newBookmark);
            // console.log("selectedMapIdx:", selectedMapIdx);

            // 즉시 UI 업데이트 - mapInfos 상태를 함수형 업데이트로 변경하여 의존성 제거
            setMapInfos((prevMapInfos) => {
                // console.log("이전 mapInfos:", prevMapInfos);

                if (selectedMapIdx === 0) {
                    // 기본 지도 북마크 업데이트
                    // console.log("기본 지도 북마크 업데이트 중...");
                    if (prevMapInfos.length > 0) {
                        const updatedBaseMap = {
                            ...prevMapInfos[0],
                            bookmark: newBookmark,
                            bookmarkCnt: newBookmark ? prevMapInfos[0].bookmarkCnt + 1 : prevMapInfos[0].bookmarkCnt - 1,
                        };
                        // console.log("업데이트된 baseMap:", updatedBaseMap);

                        const newMapInfos = [updatedBaseMap, ...prevMapInfos.slice(1)];
                        // console.log("새로운 mapInfos:", newMapInfos);
                        return newMapInfos;
                    }
                } else if (selectedMapIdx > 0 && prevMapInfos.length > selectedMapIdx) {
                    // 오버랩 지도 북마크 업데이트
                    // console.log("오버랩 지도 북마크 업데이트 중...");
                    const updatedMapInfos = prevMapInfos.map((map, index) => {
                        if (index === selectedMapIdx) {
                            const updatedMap = {
                                ...map,
                                bookmark: newBookmark,
                                bookmarkCnt: newBookmark ? map.bookmarkCnt + 1 : map.bookmarkCnt - 1,
                            };
                            // console.log(`오버랩 지도 ${index} 업데이트:`, updatedMap);
                            return updatedMap;
                        }
                        return map;
                    });

                    // console.log("새로운 mapInfos (오버랩):", updatedMapInfos);
                    return updatedMapInfos;
                }

                // console.log("북마크 업데이트 조건에 맞지 않음 - 기존 상태 유지");
                return prevMapInfos;
            });

            // console.log("=== MapScreen handleBookmarkChange 완료 ===");
        },
        [selectedMapIdx] // mapInfos 의존성 제거로 리렌더링 최적화
    );

    // 25.09.04 | 북마크 관련 리렌더링 최적화를 위한 userAct 메모이제이션
    const currentUserAct = useMemo(() => {
        const baseMapInfo = getBaseMapInfo();
        const overlapMaps = getOverlapMapInfos();

        if (selectedMapIdx > 0 && overlapMaps.length >= selectedMapIdx) {
            const selectedOverlapMap = overlapMaps[selectedMapIdx - 1];
            return {
                bookmark: selectedOverlapMap.bookmark,
                viewCnt: selectedOverlapMap.viewCnt,
                bookmarkCnt: selectedOverlapMap.bookmarkCnt,
                thumbCnt: selectedOverlapMap.thumbCnt,
            };
        } else if (baseMapInfo) {
            return {
                bookmark: baseMapInfo.bookmark,
                viewCnt: baseMapInfo.viewCnt,
                bookmarkCnt: baseMapInfo.bookmarkCnt,
                thumbCnt: baseMapInfo.thumbCnt,
            };
        }

        return {
            bookmark: false,
            viewCnt: 0,
            bookmarkCnt: 0,
            thumbCnt: 0,
        };
    }, [selectedMapIdx, mapInfos]);

    // 25.09.09 | SideRail에 전달할 오버랩 지도 정보 메모이제이션
    const overlapMapInfosForSideRail = useMemo(() => {
        const baseMapInfo = getBaseMapInfo();

        if (!baseMapInfo || !baseMapID) return undefined;

        const overlapMaps = getOverlapMapInfos();

        // 오버랩 지도가 있으면 기본 지도 + 오버랩 지도들, 없으면 기본 지도만
        if (overlapMaps.length > 0) {
            return [{ ...baseMapInfo, mapId: baseMapID } as OverlapMapInfo, ...overlapMaps.map((info) => ({ ...info } as OverlapMapInfo))];
        } else {
            return [{ ...baseMapInfo, mapId: baseMapID } as OverlapMapInfo];
        }
    }, [mapInfos, baseMapID]);

    return (
        <>
            <Helmet>
                {getBaseMapInfo() && <title>{getBaseMapInfo()!.title} - 모두의지도</title>}
                <meta name="description" content="모두의지도 개별 지도" />
            </Helmet>
            <ModalComponent />
            <div className={styles.wrapper}>
                <div className={styles.left_container} ref={sideRef}>
                    {getBaseMapInfo() && (
                        <SideRail
                            // 메모이제이션된 userAct 객체 사용으로 리렌더링 최적화
                            userAct={currentUserAct}
                            overlapMapInfos={overlapMapInfosForSideRail}
                            onBookmarkChange={handleBookmarkChange}
                            selectOtherMap={selectMap}
                            selectedMapIdx={selectedMapIdx}
                            setContent={setSideSheetCont}
                            onClearSelectedMarker={clearSelectedMarkerAndCloseSheet}
                        />
                    )}
                    {/* 사이드 시트 그룹 */}
                    {getCurrentMapInfo && (
                        <div className={`${styles.sheet_container} ${shouldSideRender ? "" : styles.sheet_hidden}`}>
                            <div className={`${styles.side_sheet} ${showSideSheet ? styles.side_sheet_slide_out : styles.side_sheet_slide_in}`}>
                                {shouldSideRender && (
                                    <SideSheet
                                        mapInfo={getCurrentMapInfo!}
                                        selectedCategory={selectedCategoryArray[selectedMapIdx] || 0}
                                        setSelectedCategory={setCurrentSelectedCategory}
                                        categories={getCurrentCategories()}
                                        shouldFloatingRender={shouldFloatingRender}
                                        showSideSheet={showSideSheet}
                                        setShowSideSheet={setShowSideSheet}
                                        shouldSideRender={shouldSideRender}
                                        // 25.07.17 | Kim Young Jun | 사이드 시트 닫힘 상태 추가
                                        isClosedSideSheet={isClosedSideSheet}
                                        setIsClosedSideSheet={setIsClosedSideSheet}
                                    >
                                        {renderContent(sideSheetCont)}
                                    </SideSheet>
                                )}
                            </div>
                            <div
                                className={`${styles.floating_side_sheet} ${shouldSideRender ? "" : styles.sheet_hidden} ${
                                    isMobileView
                                        ? showFloatingSheet
                                            ? styles.move_front
                                            : styles.move_back
                                        : showFloatingSheet
                                        ? styles.floating_side_sheet_slide_out
                                        : styles.floating_side_sheet_slide_in
                                }`}
                                ref={floatingSideRef}
                            >
                                {shouldSideRender && shouldFloatingRender && (
                                    <FloatingSideSheet
                                        changeSideSheet={changeContent}
                                        selectedPoiIdx={selectedPoiIdx}
                                        setSelectedPoiIdx={setSelectedPoiIdx}
                                        setToast={setToast}
                                        newPlace={selectedNewPlace}
                                        isAddMode={sideSheetCont === ContentList.AddMultiplePlaces}
                                        thumbState={thumbState}
                                        setThumbState={setThumbState}
                                        clickedMapId={
                                            selectedMapIdx > 0 && getOverlapMapInfos().length >= selectedMapIdx
                                                ? getOverlapMapInfos()[selectedMapIdx - 1].baseMapID
                                                : baseMapID
                                        }
                                        setShowSideSheet={setShowSideSheet}
                                        showSideSheet={showSideSheet}
                                        showFloatingSheet={showFloatingSheet}
                                        setShowFloatingSheet={setShowFloatingSheet}
                                        shouldSideRender={shouldSideRender}
                                        shouldFloatingRender={shouldFloatingRender}
                                        pageType="map"
                                        setEditPlaceStatus={setEditPlaceStatus}
                                        prevSelectedIdxRef={prevSelectedIdxRef}
                                        onPOIReload={handlePOIReload}
                                        isMobileView={isMobileView}
                                        openMapScreenModal={openModal}
                                        closeMapScreenModal={closeModal}
                                        isAnalysisActive={isAnalysisActive()}
                                        // 25.07.17 | Kim Young Jun | 사이드 시트 닫힘 상태 추가
                                        isClosedSideSheet={isClosedSideSheet}
                                        setIsClosedSideSheet={setIsClosedSideSheet}
                                        onClearSelectedMarker={clearSelectedMarkerAndCloseSheet}
                                        selectedMapIdx={selectedMapIdx}
                                    />
                                )}
                            </div>
                            <div
                                className={[
                                    styles.category_container,
                                    isMobileView && !showSideSheet && styles.wide_mobile_category,
                                    isMobileView
                                        ? showSideSheet
                                            ? styles.category_slide_default
                                            : styles.category_slide_in
                                        : showSideSheet
                                        ? showFloatingSheet
                                            ? styles.category_slide_out
                                            : styles.category_slide_default
                                        : styles.category_slide_in,
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                                style={{ width: `calc(100dvw - ${sideSheetWidth}px)` }} // 좌측 사이드 시트 너비에 따라 유동적인 카테고리 너비
                                ref={categoryContainerRef}
                            >
                                {/* 카테고리 컨테이너 너비가 90px 이상일 때만 CategoryGroup 렌더링 */}
                                {categoryContainerWidth >= 90 && (
                                    <CategoryGroup
                                        categories={getCurrentCategories()}
                                        selectedCategory={selectedCategoryArray[selectedMapIdx] || 0}
                                        setForceUpdate={setForceUpdate}
                                        prevSelectedCategoryRef={prevSelectedCategoryRef}
                                        // 장소 추가 중 카테고리 선택 시 분석도구 툴 초기화를 위해 추가 | 250901 | smyun
                                        activeIdxRef={activeIdxRef}
                                        setActiveIdx={setActiveIdx}
                                        // 25.09.02 | Kim Young Jun | 오버랩 기능에서 카테고리 배열 동기화를 위한 props 추가
                                        selectedCategoryArray={selectedCategoryArray}
                                        setSelectedCategoryArray={setSelectedCategoryArray}
                                        selectedMapIdx={selectedMapIdx}
                                        // 25.09.02 | Kim Young Jun | 오버랩 분석 동작 중인지 확인을 위한 props 추가
                                        isOverlapAnalysisActive={isOverlapActive()}
                                        onClearSelectedMarker={clearSelectedMarkerAndCloseSheet}
                                        categorySelectDisabled= {categorySelectDisabled}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {getCurrentMapInfo && !shouldSideRender && !showSideSheet && (
                    <SideSheetHandle
                        shouldSideRender={shouldSideRender}
                        showSideSheet={showSideSheet}
                        setShowSideSheet={setShowSideSheet}
                        setShowFloatingSheet={setShowFloatingSheet}
                        selectedPoiIdx={selectedPoiIdx}
                        // 25.07.17 | Kim Young Jun | 사이드 시트 닫힘 상태 추가
                        isClosedSideSheet={isClosedSideSheet}
                        setIsClosedSideSheet={setIsClosedSideSheet}
                    />
                )}

                <div className={styles.right_container}>
                    {/* <div className={styles.top} style={{ alignItems: "flex-end" }}> */}
                    <div className={styles.top}>
                        {showTopFloatingButtons && (
                            <>
                                <div className={`${styles.analysis} ${styles.btn_shadow}`}>
                                    <div className={`${styles.open}`} onClick={toggleMapToolButtons}>
                                        {"분석도구"}
                                        <div style={{ margin: "auto" }}>
                                            <Icon name="arrowDown" />
                                        </div>
                                    </div>
                                    <div className={`${styles.buttons}`}>{renderItem(analysisToolOptions)}</div>
                                </div>

                                <div className={`${styles.calculate} ${styles.btn_shadow}`}>
                                    <div className={styles.open} onClick={toggleMapToolButtons}>
                                        {"계산도구"}
                                        <div style={{ margin: "auto" }}>
                                            <Icon name="arrowDown" />
                                        </div>
                                    </div>
                                    <div className={styles.buttons}>{renderItem(measureToolOptions)}</div>
                                </div>

                                <div className={`${styles.add} ${styles.btn_shadow}`}>{renderItem(additionalToolOptions)}</div>
                            </>
                        )}
                    </div>

                    <div className={styles.bottom}>
                        <div className={styles.legendContainer}>
                            {activeIdx === MapToolType.Choropleth && renderLegend()}
                            {activeIdx === MapToolType.Region && renderCircleLegend()}
                            {activeIdx === MapToolType.Heat && (
                                <>
                                    <div className={styles.heat_bar}>{renderBlurBar()}</div>
                                    <div className={styles.heat_legend}>{renderHLegend()}</div>
                                </>
                            )}
                            {activeIdx === MapToolType.Slope && renderSlopeLegend}
                        </div>

                        <div className={styles.floatingBtnContainer}>
                            <div>
                                <FloatingButton {...mapToolOptions.gps} />
                            </div>
                            <div>
                                {mapToolOptions.zoom.map((item: FloatingBtnOptions, index) => {
                                    return <FloatingButton key={`map_${index}`} {...item} />;
                                })}
                            </div>
                        </div>
                        {/* ScaleBar 컴포넌트 */}
                    </div>
                    {currentMap && <ScaleBar map={currentMap} />}
                </div>

                {isCategoriesReady && getCurrentMapInfo && (
                    <Map
                        // 25.07.11 | Kim Young Jun | isMobileView prop 추가
                        isMobileView={isMobileView}
                        selectedPoiIdx={selectedPoiIdx}
                        setSelectedPoiIdx={setSelectedPoiIdx}
                        categories={getCurrentCategories()}
                        // 25.09.02 | Kim Young Jun | selectedCategoryArray 기반으로 변경
                        selectedCategoryArray={selectedCategoryArray}
                        activeIdx={activeIdx}
                        setActiveIdx={setActiveIdx}
                        setAnalysisXIDs={setAnalysisXIDs}
                        zoom={{
                            zoom: zoomLv,
                            setZoom: setZoomLv,
                        }}
                        isGPSOn={isGPSOn}
                        setIsGPSOn={setIsGPSOn}
                        setIsGPSLoading={setIsGPSLoading}
                        isAddMode={isAddMode}
                        mapPadding={sideSheetWidth}
                        newPlaces={newPlaceCoords}
                        setNewPlace={setNewPlaceCoords}
                        allPlaceData={allPlaceData}
                        blur={blur}
                        clearByButton={clearByButton}
                        setClearByButton={setClearByButton}
                        isExtentAnalysis={isExtentAnalysis}
                        setIsExtentAnalysis={setIsExtentAnalysis}
                        analysisStage={analysisStage}
                        setAnalysisStage={setAnalysisStage}
                        setMapProj={setMapProj}
                        startPoint={directionStartPoint} // 시간별 이동거리 시작지점
                        setDirectionStartPoint={setDirectionStartPoint}
                        directionVehicle={directionVehicle}
                        isochronePolygon={isochronePolygon}
                        setLegend={setLegend}
                        accState={accState}
                        setAccState={setAccState}
                        famousMarkers={getCurrentMapInfo?.famousMarker ?? undefined}
                        movePage={movePage}
                        // 25.09.02 | Kim Young Jun | upadateSelectedCategory 대신 setCurrentSelectedCategory 사용
                        upadateSelectedCategory={(idx?: number) => {
                            if (idx !== undefined) {
                                // idx가 지정된 경우 해당 지도의 카테고리를 현재 선택된 카테고리로 설정
                                const newArray = [...selectedCategoryArray];
                                while (newArray.length <= idx) {
                                    newArray.push(0);
                                }
                                newArray[idx] = selectedCategoryArray[selectedMapIdx] || 0;
                                setSelectedCategoryArray(newArray);
                            }
                        }}
                        forceUpdate={forceUpdate}
                        resetAcc={resetAcc}
                        setResetAcc={setResetAcc}
                        setToast={setToast}
                        analysisMetricsToolIdx={analysisMetricsToolIdx}
                        extentToolIdx={extentToolIdx}
                        dropdownSelectorItems={dropdownSelectorItems}
                        selectedDropdownField={selectedDropdownField}
                        // 25.07.07 | Kim Young Jun | 분석 트리거 인덱스 설정
                        analysisTriggerId={analysisTriggerId}
                        triggerAnalysis={triggerAnalysis}
                        prevAnalysisState={prevAnalysisState}
                        isAnalysisActive={isAnalysisActive()}
                        isNotUseMarkerAnalysis={isNotUseMarkerAnalysis()}
                        // 25.07.10 | Kim Young Jun | 단계분석, 현재 선택한 행정구역 정보 상태 추가
                        isChoroplethAnalysis={isChoroplethAnalysis}
                        setIsChoroplethAnalysis={setIsChoroplethAnalysis}
                        currentRegionCode={currentRegionCode}
                        setCurrentRegionCode={setCurrentRegionCode}
                        // 25.07.11 | Kim Young Jun | 플로팅 시트 렌더링 상태 추가
                        showFloatingSheet={showFloatingSheet}
                        setShowFloatingSheet={setShowFloatingSheet}
                        // 25.07.17 | Kim Young Jun | 사이드 시트 닫힘 상태 추가
                        isClosedSideSheet={isClosedSideSheet}
                        setIsClosedSideSheet={setIsClosedSideSheet}
                        setIsLoading={setIsLoading}
                        closeModal={closeModal}
                        sideSheetCont={sideSheetCont}
                        editPlaceStatus={editPlaceStatus}
                        setEditPlaceStatus={setEditPlaceStatus}
                        prevSelectedIdxRef={prevSelectedIdxRef}
                        setNewPlaceAddress={setNewPlaceAddress}
                        setIsOpenAgreeModal={setIsOpenAgreeModal}
                        newPlaceAddress={newPlaceAddress}
                        setRevGeoLoading={setRevGeoLoading}
                        setAgreeNoneAddress={setAgreeNoneAddress}
                        searchOptions={searchOptions}
                        setSearchOptions={setSearchOptions}
                        agreeNoneAddress={agreeNoneAddress}
                        setLoadingMessage={setLoadingMessage}
                        minSlopeZoomLv={minSlopeZoomLv}
                        onClearSelectedMarkerOnly={clearSelectedMarkerOnlyRef}
                        onClearSelectedMarkerAndCloseSheet={clearSelectedMarkerAndCloseSheetRef}
                        mapInfos={mapInfos}
                        selectedMapIdx={selectedMapIdx || 0}
                        setSelectedMapIdx={setSelectedMapIdx}
                        // 25.09.08 | Kim Young Jun | 경사도 분석 모듈화
                        slopeHook={slopeHook}
                        // ScaleBar를 위한 Map 객체 전달
                        onMapReady={setCurrentMap}
                    />
                )}

                {toast && createPortal(<Toast data={toast} setToast={setToast} />, document.body)}
                {/* 25.09.09 | 경사도 분석 활성화 시 줌 레벨 안내 토스트 */}
                {((activeIdx === MapToolType.Slope && !isOverlapActive()) || (isOverlapActive() && slopeHook.isSlopeActive)) &&
                    zoomLv < minSlopeZoomLv &&
                    createPortal(
                        <div className={styles.slopeToast}>
                            <span>경사 및 고도정보를 보기 위해서 지도를 좀 더 확대해 주세요</span>
                        </div>,
                        document.body
                    )}
                {geocodingState > -1 && geocodingState < 101 && renderProgressBar()}
                <LoadingCoverLayer isLoading={isLoading} message={loadingMessage} />
            </div>
        </>
    );
};

export default MapScreen;
